
export enum AppMode {
  HOME = 'HOME',
  SIGNIN = 'SIGNIN',
  STUDY = 'STUDY',
  TEST = 'TEST',
  CHECKER = 'CHECKER',
  INFO = 'INFO',
  CERTIFICATE = 'CERTIFICATE',
  ACCOUNT = 'ACCOUNT',
  PLAN = 'PLAN',
  API_KEY = 'API_KEY',
  BILLING = 'BILLING',
  TERMS = 'TERMS'
}

export type Theme = 'light' | 'dark';

export enum Difficulty {
  BASIC = 'Básico',
  INTERMEDIATE_1 = 'Intermedio 1',
  INTERMEDIATE_2 = 'Intermedio 2',
  ADVANCED = 'Avanzado',
  EXPERT = 'Experto / Muy Difícil'
}

export enum TestType {
  RANDOM = 'Aleatorio / Random',
  ERRORS = 'Detectar Errores',
  VOICEOVER = 'Lectura VoiceOver',
  DOCUMENTATION = 'Documentación Completa',
  FOCUS_ORDER = 'Orden de Foco',
  HEADINGS = 'Jerarquía (Headings)',
  REFLOW = 'Reflow & Zoom',
  STATES = 'Estados y Errores',
  GROUPING = 'Elementos Agrupados',
  MODALS = 'Modales & Overlays'
}

export type VisualComponentType = 
  | 'M3_TEXT_FIELD' 
  | 'M3_BUTTON' 
  | 'M3_CARD' 
  | 'M3_SWITCH' 
  | 'M3_CHIPS'
  | 'M3_CHECKBOX'
  | 'M3_RADIO_GROUP'
  | 'M3_FAB'
  | 'NONE';

export type Language = 'es' | 'en';

export interface StudyLesson {
  level: number;
  topicTag: string;
  introTitle: string;
  introSubtitle: string;
  learningPills: {
    title: string;
    description: string;
    icon: string;
    link?: string;
  }[];
  visualComponent: VisualComponentType;
  visualComponentProps?: {
    label?: string;
    placeholder?: string;
    helperText?: string;
    errorText?: string;
    buttonText?: string;
    title?: string;
    description?: string;
    ariaLabel?: string;
    role?: string;
    state?: 'default' | 'error' | 'success' | 'disabled';
    items?: string[];
    // M3 Specifics
    iconName?: string; // Material Symbol name
    secondaryText?: string;
    checked?: boolean;
  };
  documentation: {
    nonTechnical: string;
    codeExamples: {
      html: string;
      ios: string;
      android: string;
    };
    mobileScreenReader: string;
    desktopScreenReader: string;
  };
  goodPractices: string[];
  badPractices: string[];
  externalResources: {
    title: string;
    url: string;
  }[];
  test: {
    question: string;
    options: string[];
    correctIndex: number;
    successMessage: string;
    failureMessage: string;
  }[];
}

export interface AdaptiveContext {
  lastScore: number;
  trend: 'improving' | 'declining' | 'stable';
  lastTopic: string;
}

export interface UserProgress {
  maxLevel: number;
  history: {
    levelId: number;
    score: number;
    timestamp: number;
  }[];
  cachedLessons: Record<number, StudyLesson>;
}

export interface CheckerResult {
  isValidUI: boolean;
  layoutType: 'simple' | 'complex';
  screenContext?: {
    device?: string;
    screenType?: string;
    sector?: string;
    description?: string;
    language?: string;
    componentCount?: number;
  };
  globalAnnotations: {
    viewTitle: string[];
    structure: string[];
    heading: string[];
    focusOrder: string[];
  };
  complexSections: {
    regionTitle: string;
    box_2d?: number[];
    categories: {
      generalSpecific: string[];
      behavior: string[];
      alt: string[];
      grouped: string[];
    };
    components?: {
      componentTitle: string;
      box_2d?: number[];
      categories: {
        generalSpecific: string[];
        behavior: string[];
        alt: string[];
        grouped: string[];
      };
    }[];
  }[];
}

export interface TestQuestion {
  id: number;
  type: string;
  question: string;
  options: string[];
  correctAnswerIndex: number;
  explanation: string;
}

export const TRANSLATIONS = {
  es: {
    appTitle: 'AAAQuest',
    homeTitle: 'Documenta accesibilidad en segundos',
    homeSubtitle: 'Detecta errores de accesibilidad según los criterios WCAG y genera documentación lista para desarrollo con IA.',
    homeEyebrow: 'IA para accesibilidad de producto',
    featuresTitle: 'Todo lo que necesitas para documentar accesibilidad',
    featuresSubtitle: 'Convierte capturas o diseños en entregables claros para producto, diseño y desarrollo.',
    features: [
      {
        title: 'Auditoría de accesibilidad en segundos',
        desc: 'Detección automática de errores críticos del diseño y documentación según criterios de la WCAG.'
      },
      {
        title: 'Documentación lista para desarrollo',
        desc: 'Entregas funcionales de forma no técnica para la adopción en los entregables habituales de los equipos de diseño.'
      },
      {
        title: 'Multi-plataforma',
        desc: 'Detección de cualquier tipo de plataforma, web, nativo móvil, PDFs...'
      },
      {
        title: 'Unificación de contenido',
        desc: 'Genera documentación de forma unificada para todos los proyectos a lo largo del tiempo.'
      }
    ],
    workflowTitle: 'De diseño a documentación en 3 pasos',
    workflowSubtitle: 'Un flujo simple para equipos de diseño, PM y desarrollo.',
    workflowSteps: [
      { title: 'Sube una pantalla', desc: 'Captura, maqueta o frame de Figma.' },
      { title: 'IA analiza con WCAG', desc: 'Errores, contexto y severidad en segundos.' },
      { title: 'Comparte y exporta', desc: 'Pantalla documentada y analizada disponible para compartir vía PDF.' }
    ],
    pluginTitle: 'Plugin de Figma',
    pluginSubtitle: 'Documenta accesibilidad sin salir de Figma. Sincroniza frames y exporta la documentación.',
    pluginBullets: [
      'Detecta issues desde el diseño',
      'Anota criterios WCAG automáticamente',
      'Exporta entregables listos para desarrollo'
    ],
    pluginCta: 'Abrir plugin de Figma',
    pluginNote: 'Disponible próximamente',
    showcaseTitle: 'Ilustraciones y ejemplos de salida',
    showcaseSubtitle: 'Visualiza cómo se ve la documentación generada por IA.',
    pricingNav: 'Precios',
    pricingTitle: 'Planes que cubren tu necesidad',
    pricingSubtitle: 'Elige el plan que encaja con tu volumen de pantallas.',
    pricingScreensPerMonth: 'pantallas / mes',
    pricingBefore: 'Antes',
    pricingFreeTrialName: 'Prueba gratis',
    pricingFreeTrialDesc: 'Prueba AAAQuest gratis',
    pricingFreeTrialBadge: '5 análisis de pantalla · 1 usuario',
    pricingFreeTrialPrice: 'Gratis',
    pricingFreeTrialCta: 'Regístrate gratis y obtén tus intentos',
    pricingCustomName: 'Personalizado',
    pricingCustomDesc: 'Solución personalizada para equipos y operaciones avanzadas',
    pricingCustomBadge: 'Personalizado',
    pricingCustomCta: 'Contáctanos',
    pricingCustomFeatures: [
      'Número de usuarios personalizado',
      'Capacidad personalizada',
      'Documentación completa',
      'Exportación a PDF',
      'Guidelines de documentación',
      'Soporte prioritario',
      'Entrenamiento de la plataforma'
    ],
    pricingStarterName: 'Starter',
    pricingStarterDesc: 'Prueba AAAquest',
    pricingStarterBadge: '1 usuario · 50 pantallas / mes',
    pricingStarterFeatures: [
      '1 usuario',
      '50 pantallas al mes',
      'Documentación completa',
      'Exportación a PDF'
    ],
    pricingStarterIncludes: 'Incluye:',
    pricingStarterOffer: [
      'Acceso prioritario al plugin de Figma',
      'Plantillas WCAG avanzadas'
    ],
    pricingStarterCta: 'Elegir Starter',
    pricingProName: 'Agency',
    pricingProDesc: 'Automatiza el proceso de handoff de accesibilidad para tu equipo',
    pricingProBadge: 'Hasta 10 usuarios · 500 pantallas / mes en total',
    pricingProFeatures: [
      'Hasta 10 usuarios',
      '500 pantallas al mes en total',
      'Documentación completa',
      'Exportación a PDF',
      'Asistencia vía mail',
      'Guía básica de HTML'
    ],
    pricingProIncludes: 'Incluye:',
    pricingProOffer: [
      'Asistencia para equipos',
      'Mayor capacidad mensual'
    ],
    pricingProCta: 'Elegir Agency',
    pricingEnterpriseName: 'Enterprise',
    pricingEnterpriseDesc: 'Sin límite, personalizado',
    pricingEnterpriseBadge: 'Sin límite, personalizado',
    pricingEnterprisePrice: 'Personalizado',
    pricingEnterpriseFeatures: [
      'Número de usuarios personalizado',
      'Capacidad personalizada',
      'Documentación completa',
      'Exportación a PDF',
      'Asistencia 24/7',
      'Guía avanzada adaptada'
    ],
    pricingEnterpriseIncludes: 'Incluye:',
    pricingEnterpriseCta: 'Contactar',
    pricingPopular: 'Más popular',
    pricingContactOnly: 'Contactar',
    pricingPerMonth: '/ mes',
    pricingBilledAnnually: 'Facturado anualmente',
    pricingLimitedOffer: 'Oferta limitada',
    pricingFootnote: 'Todos los planes incluyen soporte por email. El cupo mensual no se acumula y se reinicia cada mes. Si se acaban los análisis mensuales no se cobra automáticamente el siguiente plan, únicamente no podrás realizar más pantallas. Siempre puedes pasar a un plan superior.',
    faqTitle: 'Preguntas frecuentes',
    faqTagComingSoon: 'Próximamente',
    faqItems: [
      {
        q: '¿Qué diferencia hay entre planes?',
        a: 'La principal diferencia es el volumen de pantallas incluidas cada mes y las opciones de colaboración. Starter es ideal para validaciones rápidas, Pro para equipos que documentan cada sprint, y Enterprise para necesidades avanzadas y soporte dedicado.',
        comingSoon: false
      },
      {
        q: '¿Cómo funciona el número de intentos restantes?',
        a: 'Cada vez que se analiza una imagen se resta un intento. En el plugin, tanto analizar una pantalla completa como analizar un componente también resta un intento. Los intentos se restablecen cada 30 días.',
        comingSoon: false
      },
      {
        q: '¿Las pantallas se acumulan mes a mes?',
        a: 'No. El cupo se reinicia al inicio de cada periodo mensual y no se acumula, para que siempre tengas un flujo estable de análisis.',
        comingSoon: false
      },
      {
        q: '¿El plugin de Figma permite trabajo colaborativo?',
        a: 'Estamos preparando flujos avanzados para equipos con historial de versiones, comentarios y seguimiento de decisiones de accesibilidad.',
        comingSoon: true
      },
      {
        q: '¿Qué incluye el plugin de Figma?',
        a: 'Incluye generación de documentación y anotaciones WCAG desde el diseño, exportación de reportes y sincronización de frames con tu repositorio de accesibilidad.',
        comingSoon: true
      },
      {
        q: '¿Cómo funciona la facturación?',
        a: 'La facturación es mensual y puedes cambiar de plan en cualquier momento. Las mejoras se aplican de inmediato y las reducciones se activan en el siguiente periodo.',
        comingSoon: false
      },
      {
        q: '¿Qué soporte está disponible?',
        a: 'Todos los planes incluyen soporte por email. Enterprise suma soporte dedicado y tiempos de respuesta acordados.',
        comingSoon: false
      },
      {
        q: '¿Incluye licencia comercial?',
        a: 'Sí, puedes usar la documentación generada en proyectos comerciales sin costes adicionales.',
        comingSoon: false
      },
      {
        q: '¿Qué datos recoge AAAQuest?',
        a: 'Recogemos los datos básicos de cuenta y facturación necesarios para operar el servicio. La información de pagos y del usuario asociada a la suscripción se gestiona en Stripe. Cuando envías una captura o diseño para análisis, se envía únicamente por API para generar la documentación y ese contenido no se guarda.',
        comingSoon: false
      },
      {
        q: '¿Se almacenan mis capturas o el contenido de mis diseños?',
        a: 'No. El contenido que envías para analizar se comparte vía API con terceros para generar la documentación y no se almacena por nuestra parte. No tenemos un acuerdo con esos terceros que garantice que no guarden esa información, igual que ocurre si envías una captura por tu cuenta a ChatGPT, Claude o Gemini, por lo que no nos hacemos responsables de ese contenido.',
        comingSoon: false
      },
      {
        q: '¿Cómo funciona la API key y cómo puedo regenerarla?',
        a: 'La API key identifica tu cuenta y tu plan cuando usas integraciones como el plugin. Puedes regenerarla o sustituirla desde el panel de API key; al hacerlo, la clave anterior deja de ser válida. Más detalle en /terms y /security.',
        comingSoon: false
      },
      {
        q: '¿Puedo usar AAAQuest sin crear cuenta?',
        a: 'Depende de la funcionalidad. El panel web y las funciones ligadas a plan o créditos requieren cuenta. En el plugin, Text Resize puede usarse sin validación de créditos, pero la detección de accesibilidad requiere API key válida y créditos disponibles.',
        comingSoon: false
      }
    ],
    contactCtaTitle: '¿Todavía tienes dudas?',
    contactCtaSubtitle: 'Escríbenos y te ayudamos a elegir el plan ideal para tu equipo.',
    contactCtaButton: 'Contactar',
    studyMode: 'Modo estudio',
    studyModeDesc: 'Aprende sobre accesibilidad digital de forma fácil y práctica en diferentes niveles',
    testMode: 'Hora del examen',
    testModeDesc: 'Realiza pruebas rápidas de accesibilidad en formato de 20 preguntas',
    checkerMode: 'Detector de accesibilidad',
    checkerModeDesc: 'Genera la documentación de accesibilidad de una captura de pantalla en segundos',
    certMode: 'Certificado',
    certModeDesc: 'Visualiza tu progreso final y obtén tu diploma de experto.',
    footer: 'Hecho con Gemini 3 por',
    changingLanguage: 'Cambiando idioma...',
    navStudy: 'Estudio',
    navTest: 'Test',
    navChecker: 'Checker',
    signIn: 'Iniciar sesión',
    signUpNav: 'Regístrate',
    signUpTitle: 'Bienvenido/a a AAAQuest',
    signUpCreateTitle: 'Crear cuenta',
    signUpForFree: 'Regístrate gratis',
    signUpPasswordTitle: 'Crea tu cuenta',
    signInPasswordTitle: 'Introduce tu contraseña',
    signUpGoogle: 'Continuar con Google',
    signUpApple: 'Continuar con Apple',
    signUpSSO: 'Single Sign-On (SSO)',
    signUpOr: 'O',
    signUpEmailPlaceholder: 'Introduce tu email',
    signUpNamePlaceholder: 'Introduce tu nombre',
    signUpEmailButton: 'Continuar con Email',
    signUpTerms: 'Al continuar, aceptas los Términos de uso y la Política de privacidad.',
    authCloseLabel: 'Cerrar',
    signUpPanelTitle: 'Genera accesibilidad con IA',
    signUpPanelSubtitle: 'Tu documentación lista en segundos.',
    authContinue: 'Continuar',
    authGenericError: 'No se pudo continuar. Revisa los datos.',
    authInvalidEmail: 'Introduce una dirección de correo válida.',
    authEmailRequired: 'El campo de correo electrónico es obligatorio.',
    authNameRequired: 'El campo de nombre es obligatorio.',
    authNameMin: 'El nombre debe contener al menos 3 caracteres.',
    accountTitle: 'Mi cuenta',
    accountBack: 'Volver',
    accountBasicInfo: 'Información básica',
    accountNameLabel: 'Nombre',
    accountEmailLabel: 'Email',
    accountEmailHint: 'El nuevo email se aplicará al guardar cambios.',
    accountEmailGoogleHint: 'No se puede modificar una cuenta registrada mediante Google.',
    accountSecurityTitle: 'Seguridad',
    accountCurrentPasswordLabel: 'Contraseña actual',
    accountCurrentPasswordPlaceholder: 'Introduce tu contraseña actual',
    accountPasswordLabel: 'Nueva contraseña',
    accountPasswordPlaceholder: 'Introduce la nueva contraseña',
    accountPasswordConfirmLabel: 'Repite la contraseña',
    accountPasswordConfirmPlaceholder: 'Vuelve a introducir la contraseña',
    accountPasswordHint: 'La contraseña debe contener al menos 8 caracteres.',
    accountSave: 'Guardar cambios',
    accountSaved: 'Cambios guardados.',
    accountNoChanges: 'No hay cambios para guardar.',
    accountNotSignedIn: 'Debes iniciar sesión para actualizar tu cuenta.',
    accountPasswordMismatch: 'Las contraseñas no coinciden.',
    accountCurrentPasswordRequired: 'Debes introducir tu contraseña actual.',
    accountCurrentPasswordInvalid: 'La contraseña actual no es correcta.',
    authInvalidCredentials: 'El correo o la contraseña no son correctos.',
    authEmailNotFound: 'No existe una cuenta con este correo.',
    authEmailExists: 'Ya existe una cuenta con este correo.',
    authPasswordRequirements: 'La contraseña debe tener al menos 6 caracteres y combinar letras y números.',
    authPasswordMin: 'La contraseña debe contener al menos 8 caracteres.',
    alreadyRegisteredToast: 'Ya estás registrado.',
    signupSuccessToast: 'Registro realizado.',
    signupSuccessPrefix: 'Registro realizado. Bienvenido/a ',
    welcomeToastPrefix: 'Bienvenido/a ',
    welcomeToastSuffix: '',
    welcomeModalTitle: 'Gracias por formar parte de AAAQuest',
    welcomeModalBodyPrimary: 'Este proyecto es reciente y puede tener fallos puntuales. Revisa siempre la documentación generada y valídala con los criterios actuales de WCAG 2.2 antes de publicarla.',
    welcomeModalBodySecondary: 'Estamos en mejora continua de la plataforma y del plugin de Figma. Agradecemos mucho todo el feedback.',
    welcomeModalBodyTertiary: 'También puedes escribirnos por email desde la opción de Contáctanos en tu perfil.',
    welcomeModalCloseCta: 'Entendido',
    welcomeModalContactCta: 'Contactar',
    launchApp: 'Abrir app',
    signInTitle: 'Accede a AAAQuest',
    signInSubtitle: 'Inicia sesión o crea una cuenta para guardar tu progreso.',
    signInEmailLabel: 'Email',
    signInPasswordLabel: 'Contraseña',
    showPassword: 'Mostrar contraseña',
    hidePassword: 'Ocultar contraseña',
    signInButton: 'Entrar',
    signUpButton: 'Crear cuenta',
    signInGoogle: 'Continuar con Google',
    signOutButton: 'Cerrar sesión',
    signInBack: 'Volver al inicio',
    signInSignedInAs: 'Sesión iniciada como',
    signInSuccess: 'Sesión iniciada correctamente.',
    signUpSuccess: 'Cuenta creada. Revisa tu email para confirmar.',
    signInError: 'No se pudo iniciar sesión.',
    signUpError: 'No se pudo crear la cuenta.',
    signOutSuccess: 'Sesión cerrada.',
    signOutError: 'No se pudo cerrar sesión.',
    studyTitle: 'Modo estudio',
    studySubtitle: 'Aprende accesibilidad mediante juegos y teoria dinamica generada con IA ajustado a tu conocimiento',
    level: 'Nivel',
    start: 'Comenzar',
    back: 'Atrás',
    next: 'Siguiente',
    confirm: 'Confirmar Respuesta',
    levelComplete: '¡Nivel Completado!',
    levelFailed: 'Nivel Fallido',
    returnSelector: 'Volver al Inicio',
    generatingLesson: 'Generando lección...',
    concepts: 'Conceptos Fundamentales',
    deepDive: 'Buenas Prácticas',
    evaluation: 'Evaluación',
    visualExample: 'Ejemplo de componente',
    tabs: { nonTech: 'Documentación Funcional', tech: 'Documentación Técnica', voiceOver: 'Lectores de Pantalla' },
    audioOutput: 'Salida de Audio',
    testTitle: 'Hora del examen',
    testSubtitle: 'Aquí tienes pruebas gratuitas sobre accesibilidad en formato de 20 preguntas rápidas y dinámicas',
    selectTopic: 'Selecciona un tema',
    selectDifficulty: 'Nivel de Dificultad',
    startTest: 'Comenzar Test',
    score: 'Puntuación',
    returnMenu: 'Volver al Menú',
    generatingTest: 'Generando Test sobre',
    question: 'Pregunta',
    explanation: 'Explicación',
    finish: 'Finalizar',
    completed: 'Test Completado',
    backBtn: 'Volver',
    checkerTitle: 'Detector de accesibilidad',
    checkerSubtitle: 'Generador de documentación de accesibilidad con solamente subir un archivo',
    uploadTitle: 'Selecciona una imagen para documentar',
    uploadDesc: 'Al seleccionar una captura de una pantalla de un producto digital, esta será analizada mediante un agente de IA para realizar la documentación de accesibilidad al completo.',
    analyzeBtn: 'Analizar captura',
    analyzing: 'Analizando...',
    analysisComplete: 'Análisis completado',
    analysisIncomplete: 'Análisis incompleto',
    uploadAnother: 'Subir otro archivo',
    invalidUI: 'No se ha detectado una interfaz válida',
    invalidUIMsg: 'La imagen subida no parece ser una captura de pantalla de un producto digital o no ha podido ser analizada correctamente.',
    tryAgain: 'Reintentar',
    context: 'Contexto Detectado',
    detectedScreen: 'Pantalla detectada',
    device: 'Dispositivo',
    sector: 'Sector',
    screenType: 'Tipo de Pantalla',
    uploadFile: 'Subir archivo',
    imageLoaded: 'Imagen cargada correctamente',
    clickToChange: 'Haz clic para cambiar',
    preview: 'Vista Previa',
    imagePlaceholder: 'La imagen subida aparecerá aquí',
    formats: 'PNG, JPG, WEBP',
    reference: 'Referencia',
    incompleteDetails: 'No se han podido generar todas las secciones. Asegúrate de que la imagen sea nítida y muestre una interfaz completa.',
    contextOptional: 'Contexto (Opcional)',
    contextPlaceholder: 'Ej: Es un modal de confirmación de compra para una app bancaria...',
    estimatedTime: 'Tiempo estimado: 12s',
    uiLanguage: 'Idioma',
    componentCount: 'Componentes detectados',
    analysisIncorrect: 'Análisis incorrecto',
    screenSize: 'Tamaño de pantalla',
    layoutType: 'Layout',
    layoutSimple: 'Simple',
    layoutComplex: 'Complejo',
    sectionsComponents: 'Secciones/Componentes',
    checkerSectionPrefix: 'Sección',
    checkerComponentPrefix: 'Componente',
    step: 'Paso',
    learnMore: 'Saber más',
    homeInfoButton: 'Ver más información',
    specAccess: 'Accesibilidad Específica',
    behavior: 'Comportamiento',
    clickToExpand: 'Mostrar detalles...',
    dos: 'Recomendado',
    donts: 'A evitar',
    correct: 'Aciertos',
    continue: 'Continuar',
    locked: 'Bloqueado',
    bestScore: 'Mejor Puntuación',
    selectLevel: 'Selecciona un Nivel',
    infoTitle: 'Accesibilidad Digital: De Cero a Héroe',
    infoSubtitle: 'Olvídate de certificaciones caras y teoría aburrida. Lo que realmente importa es saber aplicarlo, aprende cómo navegan realmente los usuarios y automatiza tu trabajo con IA.',
    learnSectionTitle: 'Aprendizaje Dinámico',
    learnSectionDesc: 'El "Modo estudio" no es un curso estático. Es un entorno vivo donde la IA genera lecciones adaptadas a tu nivel, desde conceptos básicos hasta ARIA avanzado, sin costosas certificaciones CPACC o WAS.',
    infoLevel1: "NIVEL 1",
    infoFoundations: "Fundamentos básicos",
    infoLearnList: [
      "Aprende sin complejos de forma inclusiva",
      "Contenido dinámico generado por IA según tu nivel",
      "Aprende WCAG 2.1 jugando, sin teoría aburrida"
    ],
    auditSectionTitle: 'IA vs Herramientas Tradicionales',
    auditSectionDesc: 'Herramientas como Axe DevTools, Wave o Lighthouse son excelentes para escanear código (sintaxis), pero fallan en el contexto visual. AAAQuest detecta y analiza la imagen de tu interfaz como un usuario humano. Entiende que un icono de "X" es un botón de cerrar, aunque no tenga código aún. Genera documentación PDF lista para entregar al equipo de desarrollo.',
    ctaStudy: 'Ir al modo estudio',
    ctaChecker: 'Probar detector de accesibilidad',
    whyBetter: '¿Por qué usar el detector de accesibilidad?',
    whyBetterList: [
      'Análisis visual contextual (Pixel-based), no solo de código',
      'Generación automática de PDF para entregar a clientes',
      'Detección de componentes complejos (Modales, Tablas) antes de programar',
      'Totalmente gratuito y sin instalar extensiones pesadas'
    ],
    seoTitle: 'SEO y Accesibilidad: El Vínculo Invisible',
    seoDesc: 'Google es, en esencia, un usuario ciego. Sus "bots" navegan tu web igual que un lector de pantalla. Si etiquetas correctamente tus botones, encabezados (H1-H6) y estructuras para accesibilidad, estás optimizando automáticamente tu SEO técnico. Una App o Web accesible posiciona mejor, carga más rápido y llega a más gente.',
    srTitle: 'Lectores de pantalla, la gran incógnita',
    srDesc: 'Para ser un experto, debes entender cómo se "escucha" tu interfaz. No basta con verla. Millones de usuarios dependen de software que lee el contenido en voz alta.',
    nvdaTitle: "NVDA",
    nvdaSubtitle: "Estándar en Windows",
    nvdaDesc: "El estándar de Windows. Esencial para probar la experiencia real en sistemas de escritorio. Open Source.",
    voiceOverTitle: "VoiceOver",
    voiceOverSubtitle: "macOS / iOS",
    voiceOverDesc: "Integrado nativamente. Navegación por gestos en móvil y atajos complejos en escritorio.",
    talkBackTitle: "TalkBack",
    talkBackSubtitle: "Android",
    talkBackDesc: "El lector más usado del mundo. Control táctil lineal y exploración por tacto.",
    techStackTitle: 'El Futuro de la Auditoría',
    techStackDesc: 'Deja atrás las auditorías manuales lentas. AAAQuest combines computer vision (Gemini 3) con conocimiento experto WCAG 2.1 para acelerar tu flujo de trabajo de diseño y desarrollo.',
    aboutTitle: '¿Quién está detrás de esto?',
    aboutName: 'Victor Saiz Alfageme',
    aboutRole: 'Product Designer',
    aboutDesc: 'Soy un ex-developer reconvertido a Product Designer. Por necesidad del mercado, me metí de lleno en la accesibilidad y he creado esta plataforma para ver hasta dónde puede llegar Gemini 3 para tener un producto de este estilo cara a mi pequeña comunidad.',
    comingSoon: "Próximamente más funcionalidades",
    supportBtn: 'Apoya el proyecto',
    attemptsRemaining: 'intentos restantes',
    limitReached: 'Límite alcanzado',
    certTitle: 'Certificación AAAQuest',
    certSubtitle: 'Has demostrado un dominio excepcional de las pautas de accesibilidad WCAG 2.1.',
    downloadCert: 'Descargar diploma',
    certName: 'Experto en Accesibilidad',
    certHeader: 'CERTIFICADO DE FINALIZACIÓN',
    certVerify: 'Esto certifica que',
    enterName: 'Por favor, introduzca su nombre para el diploma:',
    certText: 'Por haber completado con éxito la demo de niveles de entrenamiento intensivo en accesibilidad digital y diseño inclusivo en productos digitales.',
    lockedTitle: 'Has gastado todos los intentos de analizar capturas que tenías disponible',
    lockedDesc: 'Puedes solicitar más créditos escribiendo al creador de la herramienta Victor Saiz vía Linkedin o correo electrónico:',
    lockedThanks: 'Gracias por usar el detector de accesibildiad AAAQuest!',
    freePlanName: 'Prueba gratis',
    creditsAvailableLabel: 'créditos disponibles',
    redirectingToStripe: 'Redirigiendo a Stripe...',
    sessionExpiredError: 'Tu sesión ha caducado. Vuelve a iniciar sesión.',
    paymentSessionError: 'No pudimos abrir la pasarela de pago.',
    checkoutSyncWarning: 'Pago recibido, pero no pudimos refrescar tu plan automáticamente.',
    checkoutSyncSuccess: 'Suscripción actualizada correctamente.',
    userFallbackName: 'usuario',
    languageChangeBlockedChecker: 'El cambio de idioma no se puede aplicar a esta detección actual. Si quieres verlo en otro idioma, vuelve a hacer el análisis.',
    maintenanceBanner: 'La plataforma está en mantenimiento, puedes registrarte y te informaremos vía correo electrónico.',
    maintenanceTitle: 'Plataforma en mantenimiento',
    maintenanceBody: 'Estamos añadiendo mejoras en la plataforma. Puedes registrarte y te informaremos. Para más información puedes ver el estado en',
    captureMaintenanceBody: 'Estamos en mantenimiento. Pronto se habilitará el análisis de capturas. Te enviaremos un correo anunciándolo. Gracias por la espera.',
    maintenanceLinkLabel: 'LinkedIn',
    pricingContactModalTitle: 'Contáctanos',
    pricingContactModalBody: 'Debería abrirse una pestaña para enviar un correo a victorsaizalfageme@gmail.com. También puedes contactar por LinkedIn para reservar una llamada breve.',
    pricingContactModalPrimaryCta: 'Abrir correo',
    pricingContactModalSecondaryCta: 'Ir a LinkedIn',
    cookieDescription: 'Utilizamos cookies para mejorar tu experiencia de navegación, ofrecer contenido personalizado y analizar nuestro tráfico.',
    cookieAcceptAll: 'Aceptar todas',
    cookieRejectAll: 'Denegar todas',
    cookiePreferences: 'Preferencias',
    cookieEssentialTitle: 'Esenciales',
    cookieRequiredLabel: '(obligatorio)',
    cookieEssentialDesc: 'Necesarias para que el sitio web funcione. No se pueden desactivar.',
    cookieAnalyticsTitle: 'Analíticas',
    cookieAnalyticsDesc: 'Nos ayudan a comprender cómo interactúan los visitantes con el sitio web.',
    cookieMarketingTitle: 'Marketing',
    cookieMarketingDesc: 'Se utilizan para rastrear a los visitantes en distintos sitios web y mostrar anuncios relevantes.',
    cookieSavePreferences: 'Guardar preferencias',
    goToApp: 'Ir a la app',
    currentLanguageAriaPrefix: 'Idioma actual:',
    currentLanguageAriaSuffix: 'Haz clic para cambiar.',
    userMenuAria: 'Menú de usuario',
    userMenuAccount: 'Mi cuenta',
    userMenuPlan: 'Mi plan',
    userMenuApiKey: 'API Key',
    userMenuBilling: 'Facturación',
    userMenuTerms: 'Términos y condiciones',
    userMenuContact: 'Contáctanos',
    closeNotificationAria: 'Cerrar notificación',
    myPlanTitle: 'Mi plan',
    planTypeLabel: 'Tipo de plan',
    creditsLabel: 'Créditos',
    creditsRenewalLabel: 'Renovación de créditos',
    billingTitle: 'Facturación',
    billingCurrentPlan: 'Mi plan actual',
    billingNoActivePlan: 'Actualmente no tienes un plan de pago activo.',
    billingGoToPlan: 'Ir a Mi Plan',
    billingProfile: 'Perfil de facturación',
    billingEmail: 'Email',
    billingName: 'Nombre',
    billingPhone: 'Teléfono',
    billingPaymentMethod: 'Método de pago',
    billingInvoices: 'Facturas',
    billingInvoiceFallback: 'Factura',
    billingDownload: 'Descargar',
    billingNoInvoices: 'No hay facturas disponibles.',
    billingPrevious: 'Anterior',
    billingNext: 'Siguiente',
    billingNeedAuth: 'Necesitas iniciar sesión para ver la facturación.',
    billingLoadError: 'No pudimos cargar la información de facturación.',
    apiKeyTitle: 'API Key',
    apiKeyCardTitle: 'Tu clave API',
    apiKeyCardDesc: 'Usa esta clave para conectar el plugin de AAAQuest a tu cuenta desde AAAQuest > Set API KEY.',
    apiKeySecurityNote: 'Si necesitas volver a copiar la API key, tendrás que regenerarla por seguridad.',
    apiKeyRotate: 'Regenerar',
    apiKeyValueLabel: 'Clave de API',
    apiKeyCopyCta: 'Copiar en portapapeles',
    apiKeyCopyAria: 'Copiar API Key',
    apiKeyShowAria: 'Mostrar API Key',
    apiKeyHideAria: 'Ocultar API Key',
    apiKeyCopied: 'Copiada en el portapapeles.',
    apiKeyNeedAuth: 'Necesitas iniciar sesión para ver tu API Key.',
    apiKeyLoadError: 'No hemos podido cargar tu API Key.',
    apiKeyCopyError: 'No se pudo copiar la API Key.',
    apiKeyRotateNeedAuth: 'Necesitas iniciar sesión para regenerar tu API Key.',
    apiKeyRotateError: 'No se pudo regenerar la API Key.',
    apiKeyRotateModalTitle: 'Regenerar API Key',
    apiKeyRotateModalBody: 'Si tienes la API key vinculada en Figma, tendrás que cambiarla manualmente. ¿Estás seguro de regenerar la API key?',
    apiKeyRotateConfirm: 'Regenerar API key',
    termsTitle: 'Términos y condiciones',
    termsUpdated: 'Última actualización: 22 de febrero de 2026.',
    termsSections: [
      {
        title: '1. Identificación',
        body: 'AAAQuest es una plataforma digital propiedad de su titular (en adelante, “AAAQuest”, “nosotros”). Puedes contactarnos en victorsaizalfageme@gmail.com.',
      },
      {
        title: '2. Aceptación de los términos',
        body: 'Al acceder y usar AAAQuest, aceptas estos términos y nuestra política de privacidad. Si no estás de acuerdo, no debes utilizar la plataforma.',
      },
      {
        title: '3. Registro y cuenta',
        body: 'Debes proporcionar información veraz y mantenerla actualizada. Eres responsable del uso de tu cuenta y de la confidencialidad de tus credenciales.',
      },
      {
        title: '4. Planes, pagos y facturación',
        body: 'AAAQuest ofrece un plan gratuito y planes de pago. Los precios y condiciones se muestran en la plataforma y pueden cambiar. Las compras se procesan mediante Stripe. El acceso a funcionalidades depende del plan activo.',
      },
      {
        title: '5. Uso permitido',
        body: 'No está permitido usar la plataforma con fines ilícitos, vulnerar derechos de terceros, intentar acceder a cuentas ajenas, ni realizar acciones que dañen, alteren o comprometan el servicio.',
      },
      {
        title: '6. Propiedad intelectual',
        body: 'Todo el contenido, marca, diseño y software de AAAQuest es propiedad de su titular y está protegido por las leyes de propiedad intelectual. No se concede ninguna licencia salvo la de uso normal del servicio.',
      },
      {
        title: '7. Limitación de responsabilidad',
        body: 'AAAQuest se ofrece “tal cual”. En la medida permitida por la ley, no garantizamos la ausencia de errores ni la disponibilidad ininterrumpida del servicio. No seremos responsables por daños indirectos o pérdida de datos.',
      },
      {
        title: '8. Protección de datos',
        body: 'Tratamos tus datos conforme a la legislación aplicable y nuestra política de privacidad. Puedes solicitar la eliminación de tu cuenta y datos en cualquier momento.',
      },
      {
        title: '9. Datos que recopilamos',
        body: 'Recopilamos los datos básicos de cuenta y los datos de facturación necesarios para operar el servicio. La información de pagos y del usuario asociada a la suscripción se gestiona mediante Stripe. Cuando el usuario envía capturas o fragmentos de diseños para análisis, ese contenido se transmite únicamente por API para generar la documentación y no se almacena.',
      },
      {
        title: '10. API key, funcionalidades y control de acceso',
        body: 'La API key permite identificar la cuenta y su plan en integraciones autorizadas. El usuario puede regenerarla o revocarla desde la sección correspondiente del producto; al hacerlo, la clave anterior debe dejar de utilizarse. Text Resize puede utilizarse sin validación de créditos, mientras que la detección de accesibilidad requiere API key válida y créditos disponibles. El acceso se limita mediante controles de autenticación y autorización razonables, junto con cifrado en tránsito entre cliente y servicios.',
      },
      {
        title: '11. Derechos de rectificación, borrado y contacto de privacidad',
        body: 'El usuario puede solicitar acceso, rectificación o borrado de sus datos escribiendo a privacidad@aaaquest.com o mediante /contact. Atenderemos las solicitudes conforme a la normativa aplicable, incluida la lógica general de GDPR y CCPA en lenguaje comprensible, sin perjuicio de retenciones necesarias por obligación legal, seguridad o prevención de fraude.',
      },
      {
        title: '12. Sin cuenta y uso responsable',
        body: 'Algunas funcionalidades pueden requerir cuenta, sesión activa o API key, mientras que otras pueden ofrecerse de forma limitada sin validación de créditos. El panel web requiere cuenta para operar con normalidad y el acceso a funciones de análisis depende del plan activo o de la API key correspondiente. El usuario se compromete a no subir capturas con información sensible innecesaria, secretos, credenciales o datos personales de terceros salvo que tenga base legítima para ello.',
      },
      {
        title: '13. Modificaciones',
        body: 'Podemos actualizar estos términos. Si continúas usando la plataforma después de los cambios, se considerará que aceptas los nuevos términos.',
      },
      {
        title: '14. Ley aplicable',
        body: 'Estos términos se rigen por la legislación española. Para cualquier disputa, las partes se someterán a los juzgados y tribunales de Barcelona, salvo norma imperativa distinta.',
      },
    ],
    contactTitle: 'Contáctanos',
    contactSubtitle: 'Cuéntanos tu necesidad y te responderemos lo antes posible.',
    contactAuthNotice: 'Para enviar el mensaje desde tu correo registrado, inicia sesión primero.',
    contactSubjectLabel: 'Asunto',
    contactSubjectPlaceholder: 'Ej: Necesitamos una demo para el equipo',
    contactSubjectRequired: 'El asunto es obligatorio.',
    contactMessageLabel: 'Mensaje',
    contactMessagePlaceholder: 'Cuéntanos qué necesitas, número de personas, plazos, etc.',
    contactMessageRequired: 'El mensaje es obligatorio.',
    contactNeedSignIn: 'Inicia sesión para enviar desde tu correo registrado.',
    contactMailNoticePrefix: 'Se abrirá tu cliente de correo para enviar el mensaje desde',
    contactCancel: 'Cancelar',
    contactSend: 'Enviar',
    contactDefaultUser: 'Usuario',
    contactBodyMessageLabel: 'Mensaje:',
    contactBodySentByLabel: 'Enviado por',
    accountPhoneLabel: 'Teléfono',
    accountPhoneHint: 'Formato internacional con prefijo (ejemplo +34 633 123 456)',
    accountSecurityDesc: 'Actualiza tu contraseña para mantener tu cuenta segura.',
    accountChangePassword: 'Cambiar contraseña',
    accountPhoneFormatError: 'Error al guardar el teléfono. Usa prefijo internacional con +, por ejemplo +34.',
    accountPhoneDuplicateError: 'Error al guardar el teléfono. Ese número ya está en uso.',
    accountSaveError: 'Error al guardar los cambios.',
    accountPasswordModalTitle: 'Cambiar contraseña',
    accountSavePassword: 'Guardar contraseña',
    analysisErrorUnsupportedFormat: 'Formato no soportado. Usa PNG, JPG o WEBP.',
    analysisErrorTryAgain: 'Error en el análisis. Inténtalo de nuevo.',
    analysisErrorNoCredits: 'No tienes créditos disponibles. Revisa tu plan para seguir analizando.',
    analysisErrorCreditsSync: 'El análisis se completó, pero no pudimos actualizar los créditos.',
    checkerDropFile: 'Suelta el archivo aquí',
    checkerPdfTitle: 'Auditoría AAAQuest',
    checkerPdfFooter: 'Hecho con Gemini 3 por Victor Saiz Alfageme',
    checkerPdfScreenSummary: 'Resumen de pantalla',
    checkerPdfGlobalAccessibility: 'Accesibilidad global',
    checkerPdfViewTitle: 'TÍTULO (Title)',
    checkerPdfStructure: 'ESTRUCTURA (Landmarks)',
    checkerPdfHeading: 'ENCABEZADOS (Headings)',
    checkerPdfFocusOrder: 'ORDEN DE FOCO',
    checkerPdfSectionsDetected: 'Secciones / Componentes detectados',
    checkerPdfGeneral: 'General',
    checkerPdfBehavior: 'Comportamiento',
    checkerPdfAlt: 'Alt / Imágenes',
    checkerPdfGrouped: 'Agrupación',
    checkerSplitView: 'Vista dividida',
    checkerFullView: 'Vista completa',
    checkerSwitchToSplitView: 'Cambiar a vista dividida',
    checkerSwitchToFullView: 'Cambiar a vista completa',
    checkerDownloadPdf: 'Descargar PDF',
    checkerReportFileName: 'AAAQuest-Informe-Accesibilidad.pdf',
    checkerSeeAll: 'Ver todo',
    checkerNoSectionsDetected: 'No se detectaron secciones específicas.',
    signInEmailPlaceholder: 'tu@email.com',
    testGenerateError: 'Error generando el test. Inténtalo de nuevo.',
    testTopicRandom: 'Aleatorio',
    testTopicErrors: 'Detectar errores',
    testTopicVoiceOver: 'Lectura VoiceOver',
    testTopicDocumentation: 'Documentación completa',
    testTopicFocusOrder: 'Orden de foco',
    testTopicHeadings: 'Jerarquía (Headings)',
    testTopicReflow: 'Reflow y zoom',
    testTopicStates: 'Estados y errores',
    testTopicGrouping: 'Elementos agrupados',
    testTopicModals: 'Modales y overlays',
    difficultyBasic: 'Básico',
    difficultyIntermediate1: 'Intermedio 1',
    difficultyIntermediate2: 'Intermedio 2',
    difficultyAdvanced: 'Avanzado',
    difficultyExpert: 'Experto / Muy difícil',
    studyScreenReaderMobileLabel: 'Mobile (VoiceOver/TalkBack)',
    studyScreenReaderDesktopLabel: 'Desktop (NVDA)',
    studyExperienceMobile: 'Experiencia táctil (mobile)',
    studyExperienceDesktop: 'Experiencia de teclado (desktop)'
  },
  en: {
    appTitle: 'AAAQuest',
    homeTitle: 'Document accessibility in seconds',
    homeSubtitle: 'Detect accessibility issues using WCAG criteria and generate development-ready documentation with AI.',
    homeEyebrow: 'AI for product accessibility',
    featuresTitle: 'Everything you need to document accessibility',
    featuresSubtitle: 'Turn screenshots or designs into clear deliverables for product, design, and engineering.',
    features: [
      {
        title: 'Accessibility audit in seconds',
        desc: 'Automatic detection of critical design issues with WCAG-aligned documentation.'
      },
      {
        title: 'Development-ready documentation',
        desc: 'Non-technical deliverables tailored to the standard outputs design teams already use.'
      },
      {
        title: 'Multi-platform',
        desc: 'Detection for any platform: web, native mobile, PDFs...'
      },
      {
        title: 'Unified documentation',
        desc: 'Generate consistent documentation across all projects over time.'
      }
    ],
    workflowTitle: 'From design to documentation in 3 steps',
    workflowSubtitle: 'A simple flow for design, PM, and engineering teams.',
    workflowSteps: [
      { title: 'Upload a screen', desc: 'Screenshot, mockup, or Figma frame.' },
      { title: 'AI checks WCAG', desc: 'Issues, context, and severity in seconds.' },
      { title: 'Share and export', desc: 'PDF, checklist, or notes for your team.' }
    ],
    pluginTitle: 'Figma plugin',
    pluginSubtitle: 'Document accessibility without leaving Figma. Sync frames and export reports.',
    pluginBullets: [
      'Detect issues directly from design',
      'Auto-annotate WCAG criteria',
      'Export dev-ready deliverables'
    ],
    pluginCta: 'Open Figma plugin',
    pluginNote: 'Coming soon',
    showcaseTitle: 'Illustrations and output previews',
    showcaseSubtitle: 'See how AI-generated documentation looks.',
    pricingNav: 'Pricing',
    pricingTitle: 'Plans that fit your needs',
    pricingSubtitle: 'Choose the plan that matches your screen volume.',
    pricingScreensPerMonth: 'screens / month',
    pricingBefore: 'Before',
    pricingFreeTrialName: 'Free trial',
    pricingFreeTrialDesc: 'Try AAAQuest for Free',
    pricingFreeTrialBadge: '5 screen analyses · 1 user',
    pricingFreeTrialPrice: 'Free',
    pricingFreeTrialCta: 'Sign up for free and get your tries',
    pricingCustomName: 'Custom',
    pricingCustomDesc: 'Custom solution for teams and advanced operations',
    pricingCustomBadge: 'Custom',
    pricingCustomCta: 'Contact us',
    pricingCustomFeatures: [
      'Custom number of users',
      'Custom capacity',
      'Complete documentation',
      'PDF export',
      'Documentation guidelines',
      'Priority support',
      'Platform training'
    ],
    pricingStarterName: 'Starter',
    pricingStarterDesc: 'Try AAAQuest',
    pricingStarterBadge: '1 user · 50 screens / month',
    pricingStarterFeatures: [
      '1 user',
      '50 screens per month',
      'Complete documentation',
      'PDF export'
    ],
    pricingStarterIncludes: 'Includes:',
    pricingStarterOffer: [
      'Priority access to the Figma plugin',
      'Advanced WCAG templates'
    ],
    pricingStarterCta: 'Choose Starter',
    pricingProName: 'Agency',
    pricingProDesc: 'Automate the accessibility handoff process for your team',
    pricingProBadge: 'Up to 10 users · 500 screens / month total',
    pricingProFeatures: [
      'Up to 10 users',
      '500 screens per month total',
      'Complete documentation',
      'PDF export',
      'Email assistance',
      'Basic accessibility guide'
    ],
    pricingProIncludes: 'Includes:',
    pricingProOffer: [
      'Team assistance',
      'Higher monthly capacity'
    ],
    pricingProCta: 'Choose Agency',
    pricingEnterpriseName: 'Enterprise',
    pricingEnterpriseDesc: 'Unlimited, custom',
    pricingEnterpriseBadge: 'Unlimited, custom',
    pricingEnterprisePrice: 'Custom',
    pricingEnterpriseFeatures: [
      'Custom number of users',
      'Custom capacity',
      'Complete documentation',
      'PDF export',
      '24/7 assistance',
      'Advanced tailored guide'
    ],
    pricingEnterpriseIncludes: 'Includes:',
    pricingEnterpriseCta: 'Contact us',
    pricingPopular: 'Most popular',
    pricingContactOnly: 'Contact us',
    pricingPerMonth: '/ month',
    pricingBilledAnnually: 'Billed annually',
    pricingLimitedOffer: 'Limited offer',
    pricingFootnote: 'All plans include email support. Monthly quota does not roll over and resets every month. If you run out of monthly analyses, you will not be charged for the next plan automatically; you simply will not be able to analyze more screens. You can always upgrade to a higher plan.',
    faqTitle: 'FAQ',
    faqTagComingSoon: 'Coming soon',
    faqItems: [
      {
        q: 'What is the difference between plans?',
        a: 'The key difference is monthly screen volume and collaboration depth. Starter fits quick validations, Pro supports sprint-level teamwork, and Enterprise adds advanced workflows and dedicated support.',
        comingSoon: false
      },
      {
        q: 'How are screens computed?',
        a: 'Each time you analyze a screen it consumes one unit. Re-analyzing after meaningful design changes counts as a new unit.',
        comingSoon: false
      },
      {
        q: 'Do screens roll over month to month?',
        a: 'No. The quota resets at the start of each monthly period to keep a steady analysis flow.',
        comingSoon: false
      },
      {
        q: 'Can the Figma plugin support team workflows?',
        a: 'We are preparing advanced team flows with version history, comments, and accessibility decision tracking.',
        comingSoon: true
      },
      {
        q: 'What does the Figma plugin include?',
        a: 'It includes WCAG annotations and documentation directly from design files, plus report export and frame syncing.',
        comingSoon: true
      },
      {
        q: 'How does billing work?',
        a: 'Monthly billing with the ability to upgrade anytime. Downgrades take effect on the next cycle.',
        comingSoon: false
      },
      {
        q: 'What support is available?',
        a: 'Email support is included for all plans, with dedicated support and SLAs for Enterprise.',
        comingSoon: false
      },
      {
        q: 'Do plans include a commercial license?',
        a: 'Yes, you can use generated documentation commercially with no extra fees.',
        comingSoon: false
      },
      {
        q: 'What data does AAAQuest collect?',
        a: 'We collect the basic account and billing data needed to operate the service. Payment information and the user data tied to the subscription are managed in Stripe. When you send a screenshot or design for analysis, it is sent only through the API to generate the documentation and that content is not stored.',
        comingSoon: false
      },
      {
        q: 'Are screenshots or design contents stored?',
        a: 'No. The content you send for analysis is shared via API with third parties to generate the documentation and is not stored by us. We do not have an agreement with those third parties guaranteeing that they will not retain that information, just as if you manually sent a screenshot to ChatGPT, Claude, or Gemini, so we are not responsible for that content.',
        comingSoon: false
      },
      {
        q: 'How does the API key work and how can I regenerate it?',
        a: 'The API key identifies your account and plan when you use integrations such as the plugin. You can rotate or replace it from the API key panel; once rotated, the previous key should no longer be used. More detail in /terms and /security.',
        comingSoon: false
      },
      {
        q: 'How do remaining attempts work?',
        a: 'Each time an image is analyzed, one attempt is deducted. In the plugin, analyzing a full screen or a single component also deducts one attempt. Attempts reset every 30 days.',
        comingSoon: false
      },
      {
        q: 'Can I use AAAQuest without creating an account?',
        a: 'It depends on the feature. The web panel and any plan- or credit-based functionality require an account. In the plugin, Text Resize can be used without credit validation, but accessibility detection requires a valid API key and available credits.',
        comingSoon: false
      }
    ],
    contactCtaTitle: 'Still have questions?',
    contactCtaSubtitle: 'Reach out and we will help you pick the right plan.',
    contactCtaButton: 'Contact sales',
    studyMode: 'Study Mode',
    studyModeDesc: 'Learn about digital accessibility easily and practically across different levels.',
    testMode: 'Exam Time',
    testModeDesc: 'Take quick accessibility tests in a 20-question format',
    checkerMode: 'Accessibility Detector',
    checkerModeDesc: 'Generate accessibility documentation from a screenshot in seconds',
    certMode: 'Certificate',
    certModeDesc: 'View your final progress and get your expert diploma.',
    footer: 'Made with Gemini 3 by',
    changingLanguage: 'Changing language...',
    navStudy: 'Study',
    navTest: 'Test',
    navChecker: 'Checker',
    signIn: 'Sign in',
    signUpNav: 'Sign up',
    signUpTitle: 'Welcome to AAAQuest',
    signUpCreateTitle: 'Create account',
    signUpForFree: 'Sign up for free',
    signUpPasswordTitle: 'Create your account',
    signInPasswordTitle: 'Enter your password',
    signUpGoogle: 'Continue with Google',
    signUpApple: 'Continue with Apple',
    signUpSSO: 'Single Sign-On (SSO)',
    signUpOr: 'OR',
    signUpEmailPlaceholder: 'Enter your email',
    signUpNamePlaceholder: 'Enter your name',
    signUpEmailButton: 'Continue with Email',
    signUpTerms: 'By continuing, you agree to the Terms of Use & Privacy Policy.',
    authCloseLabel: 'Close',
    signUpPanelTitle: 'Generate accessibility with AI',
    signUpPanelSubtitle: 'Your documentation ready in seconds.',
    authContinue: 'Continue',
    authGenericError: 'We could not continue. Check your details.',
    authInvalidEmail: 'Enter a valid email address.',
    authEmailRequired: 'Email is required.',
    authNameRequired: 'Name is required.',
    authNameMin: 'Name must be at least 3 characters.',
    accountTitle: 'Personal settings',
    accountBack: 'Back',
    accountBasicInfo: 'Basic information',
    accountNameLabel: 'Name',
    accountEmailLabel: 'Email',
    accountEmailHint: 'The new email will be applied after saving.',
    accountEmailGoogleHint: 'You cannot modify a Google-registered account.',
    accountSecurityTitle: 'Security',
    accountCurrentPasswordLabel: 'Current password',
    accountCurrentPasswordPlaceholder: 'Enter your current password',
    accountPasswordLabel: 'New password',
    accountPasswordPlaceholder: 'Enter the new password',
    accountPasswordConfirmLabel: 'Confirm password',
    accountPasswordConfirmPlaceholder: 'Re-enter the password',
    accountPasswordHint: 'Password must be at least 8 characters.',
    accountSave: 'Save changes',
    accountSaved: 'Changes saved.',
    accountNoChanges: 'No changes to save.',
    accountNotSignedIn: 'You must sign in to update your account.',
    accountPasswordMismatch: 'Passwords do not match.',
    accountCurrentPasswordRequired: 'You must enter your current password.',
    accountCurrentPasswordInvalid: 'Current password is incorrect.',
    authInvalidCredentials: 'Email or password is incorrect.',
    authEmailNotFound: 'No account found for this email.',
    authEmailExists: 'An account with this email already exists.',
    authPasswordRequirements: 'Password must be at least 6 characters and include letters and numbers.',
    authPasswordMin: 'Password must be at least 8 characters.',
    alreadyRegisteredToast: 'You are already registered.',
    signupSuccessToast: 'Registration completed.',
    signupSuccessPrefix: 'Registration completed. Welcome ',
    welcomeToastPrefix: 'Welcome ',
    welcomeToastSuffix: '',
    welcomeModalTitle: 'Thanks for being part of AAAQuest',
    welcomeModalBodyPrimary: 'This project is still evolving and occasional issues may happen. Always review the generated documentation and validate it against current WCAG 2.2 criteria before shipping.',
    welcomeModalBodySecondary: 'We are continuously improving the platform and the Figma plugin, and we truly appreciate all feedback.',
    welcomeModalBodyTertiary: 'You can also reach us by email from the Contact option in your profile.',
    welcomeModalCloseCta: 'Got it',
    welcomeModalContactCta: 'Contact',
    launchApp: 'Launch app',
    signInTitle: 'Sign in to AAAQuest',
    signInSubtitle: 'Sign in or create an account to save your progress.',
    signInEmailLabel: 'Email',
    signInPasswordLabel: 'Password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    signInButton: 'Enter',
    signUpButton: 'Create account',
    signInGoogle: 'Continue with Google',
    signOutButton: 'Sign out',
    signInBack: 'Back to home',
    signInSignedInAs: 'Signed in as',
    signInSuccess: 'Signed in successfully.',
    signUpSuccess: 'Account created. Check your email to confirm.',
    signInError: 'Could not sign in.',
    signUpError: 'Could not create account.',
    signOutSuccess: 'Signed out.',
    signOutError: 'Could not sign out.',
    studyTitle: 'Study Mode',
    studySubtitle: 'Learn accessibility from zero to hero through games and dynamic AI-generated theory',
    level: 'Level',
    start: 'Start',
    back: 'Back',
    next: 'Next',
    confirm: 'Confirm Answer',
    levelComplete: 'Level Completed!',
    levelFailed: 'Level Failed',
    returnSelector: 'Return to Start',
    generatingLesson: 'Generating lesson...',
    concepts: 'Key Concepts',
    deepDive: 'Best Practices',
    evaluation: 'Evaluation',
    visualExample: 'Component Example',
    tabs: { nonTech: 'Functional Spec', tech: 'Technical Code', voiceOver: 'Screen Readers' },
    audioOutput: 'Audio Output',
    testTitle: 'Exam Time',
    testSubtitle: 'Here are free accessibility tests in a quick and dynamic 20-question format',
    selectTopic: 'Select a Topic',
    selectDifficulty: 'Difficulty Level',
    startTest: 'Start Test',
    score: 'Score',
    returnMenu: 'Return to Menu',
    generatingTest: 'Generating Test on',
    question: 'Question',
    explanation: 'Explanation',
    finish: 'Finish',
    completed: 'Test Completed',
    backBtn: 'Back',
    checkerTitle: 'Accessibility Detector',
    checkerSubtitle: 'Accessibility documentation generator by just uploading a file',
    uploadTitle: 'Select an image to document',
    uploadDesc: 'When selecting a screenshot of a digital product, it will be analyzed by an AI agent to perform complete accessibility documentation.',
    analyzeBtn: 'Analyze Screenshot',
    analyzing: 'Analyzing...',
    analysisComplete: 'Analysis Completed',
    analysisIncomplete: 'Analysis Incomplete',
    uploadAnother: 'Upload another file',
    invalidUI: 'No valid UI detected',
    invalidUIMsg: 'The uploaded image does not appear to be a screenshot of a digital product or could not be analyzed correctly.',
    tryAgain: 'Try Again',
    context: 'Detected Context',
    detectedScreen: 'Detected Screen',
    device: 'Device',
    sector: 'Sector',
    screenType: 'Screen Type',
    uploadFile: 'Upload file',
    imageLoaded: 'Image uploaded successfully',
    clickToChange: 'Click to change',
    preview: 'Preview',
    imagePlaceholder: 'Uploaded image will appear here',
    formats: 'PNG, JPG, WEBP',
    reference: 'Reference',
    incompleteDetails: 'Could not generate all sections. Ensure the image is sharp and shows a complete interface.',
    contextOptional: 'Context (Optional)',
    contextPlaceholder: 'Ex: It is a purchase confirmation modal for a banking app...',
    estimatedTime: 'Estimated time: 12s',
    uiLanguage: 'Language',
    componentCount: 'Detected Components',
    analysisIncorrect: 'Analysis incorrect',
    screenSize: 'Screen Size',
    layoutType: 'Layout',
    layoutSimple: 'Simple',
    layoutComplex: 'Complex',
    sectionsComponents: 'Sections/Components',
    checkerSectionPrefix: 'Section',
    checkerComponentPrefix: 'Component',
    step: 'Step',
    learnMore: 'Learn More',
    homeInfoButton: 'Discover more info',
    specAccess: 'Specific Accessibility',
    behavior: 'Behavior',
    clickToExpand: 'Click to expand...',
    dos: "Do's",
    donts: "Don'ts",
    correct: 'Correct',
    continue: 'Continue',
    locked: 'Locked',
    bestScore: 'Best Score',
    selectLevel: 'Select a Level',
    infoTitle: 'Digital Accessibility: Zero to Hero',
    infoSubtitle: 'No expensive courses. No complex certifications. Learn for free and audit with AI.',
    learnSectionTitle: 'Assistive Tools',
    learnSectionDesc: 'Master essential tools like NVDA, VoiceOver, and TalkBack. Understand how users truly navigate without relying on sight.',
    infoLevel1: "LEVEL 1",
    infoFoundations: "Basic Foundations",
    infoLearnList: [
      "Learn inclusively without complexes",
      "Dynamic content generated by AI based on your level",
      "Learn WCAG 2.1 by playing, no boring theory"
    ],
    auditSectionTitle: 'AI Visual Audit',
    auditSectionDesc: 'Tools like Axe or Wave are great for code, but AAAQuest understands visual context. Generate full PDF reports of your designs instantly.',
    ctaStudy: 'Start Learning',
    ctaChecker: 'Launch AI Detector',
    whyBetter: 'Why use the AI Detector?',
    whyBetterList: [
      'Contextual visual analysis, not just code syntax',
      'Automatic PDF generation for client delivery',
      'Detection of complex components (Modals, Tables)',
      'Free and no extension installation required'
    ],
    seoTitle: 'SEO & Accessibility: The Invisible Link',
    seoDesc: 'Google does not "see" your web, it "reads" it. Just like a screen reader user. A semantically correct web for accessibility is perfectly optimized for search engines. Structure, headings, and ARIA tags improve your ranking.',
    srTitle: 'Screen Readers, the great unknown',
    srDesc: 'Automated tools detect 30% of errors. The remaining 70% depends on user experience. Does the focus travel in logical order? Are modals announced? Learn to validate real UX.',
    nvdaTitle: "NVDA",
    nvdaSubtitle: "Windows Standard",
    nvdaDesc: "The Windows standard. Essential for testing real experience on desktop systems. Open Source.",
    voiceOverTitle: "VoiceOver",
    voiceOverSubtitle: "macOS / iOS",
    voiceOverDesc: "Natively integrated. Gesture navigation on mobile and complex shortcuts on desktop.",
    talkBackTitle: "TalkBack",
    talkBackSubtitle: "Android",
    talkBackDesc: "The world's most used reader. Linear touch control and touch exploration.",
    techStackTitle: 'Future Tech Stack',
    techStackDesc: 'Leave slow manual audits behind. AAAQuest combines computer vision (Gemini 2.5) with WCAG 2.1 expert knowledge to accelerate your design and development workflow.',
    aboutTitle: "Who's behind this?",
    aboutName: 'Victor Saiz Alfageme',
    aboutRole: 'Product Designer',
    aboutDesc: "I am an ex-developer turned Product Designer. Out of market necessity, I dove deep into accessibility and created this platform to see how far Gemini 3 can go to provide a tool like this for my small community.",
    comingSoon: "More features coming soon",
    supportBtn: 'Support the project',
    attemptsRemaining: 'attempts remaining',
    limitReached: 'Limit reached',
    certTitle: 'AAAQuest Certification',
    certSubtitle: 'You have demonstrated exceptional mastery of WCAG 2.1 accessibility guidelines.',
    downloadCert: 'Download Diploma',
    certName: 'Accessibility Expert',
    certHeader: 'CERTIFICATE OF COMPLETION',
    certVerify: 'This certifies that',
    enterName: 'Please enter your name for the certificate:',
    certText: 'For successfully completing the demo levels of intensive training in digital accessibility and inclusive design in digital products.',
    lockedTitle: 'You have used all available screenshot analysis attempts',
    lockedDesc: 'You can request more credits by writing to the tool creator Victor Saiz Alfageme via LinkedIn or email:',
    lockedThanks: 'Thanks for using AAAQuest Accessibility Detector!',
    freePlanName: 'Free trial',
    creditsAvailableLabel: 'credits available',
    redirectingToStripe: 'Redirecting to Stripe...',
    sessionExpiredError: 'Your session has expired. Please sign in again.',
    paymentSessionError: 'We could not open the checkout page.',
    checkoutSyncWarning: 'Payment received, but we could not refresh your plan automatically.',
    checkoutSyncSuccess: 'Subscription updated successfully.',
    userFallbackName: 'user',
    languageChangeBlockedChecker: 'The language change cannot be applied to the current detection. If you want it in another language, run the analysis again.',
    maintenanceBanner: 'The platform is under maintenance. You can sign up and we will notify you by email.',
    maintenanceTitle: 'Platform under maintenance',
    maintenanceBody: 'We are shipping improvements to the platform. You can sign up and we will keep you informed. For more information, check the status on',
    captureMaintenanceBody: 'We are under maintenance. Screenshot analysis will be enabled soon. We will email you when it is available. Thank you for your patience.',
    maintenanceLinkLabel: 'LinkedIn',
    pricingContactModalTitle: 'Contact us',
    pricingContactModalBody: 'A new tab should open so you can send an email to victorsaizalfageme@gmail.com. You can also contact us on LinkedIn to book a short call.',
    pricingContactModalPrimaryCta: 'Open email',
    pricingContactModalSecondaryCta: 'Open LinkedIn',
    cookieDescription: 'We use cookies to improve your browsing experience, offer personalized content, and analyze our traffic.',
    cookieAcceptAll: 'Accept all',
    cookieRejectAll: 'Reject all',
    cookiePreferences: 'Preferences',
    cookieEssentialTitle: 'Essential',
    cookieRequiredLabel: '(required)',
    cookieEssentialDesc: 'Required for the website to work. They cannot be disabled.',
    cookieAnalyticsTitle: 'Analytics',
    cookieAnalyticsDesc: 'They help us understand how visitors interact with the website.',
    cookieMarketingTitle: 'Marketing',
    cookieMarketingDesc: 'They are used to track visitors across websites in order to show relevant ads.',
    cookieSavePreferences: 'Save preferences',
    goToApp: 'Go to the app',
    currentLanguageAriaPrefix: 'Current language:',
    currentLanguageAriaSuffix: 'Click to change.',
    userMenuAria: 'User menu',
    userMenuAccount: 'My account',
    userMenuPlan: 'My plan',
    userMenuApiKey: 'API key',
    userMenuBilling: 'Billing',
    userMenuTerms: 'Terms and conditions',
    userMenuContact: 'Contact us',
    closeNotificationAria: 'Close notification',
    myPlanTitle: 'My plan',
    planTypeLabel: 'Plan type',
    creditsLabel: 'Credits',
    creditsRenewalLabel: 'Credits renewal',
    billingTitle: 'Billing',
    billingCurrentPlan: 'Current plan',
    billingNoActivePlan: 'You do not have an active paid plan right now.',
    billingGoToPlan: 'Go to My Plan',
    billingProfile: 'Billing profile',
    billingEmail: 'Email',
    billingName: 'Name',
    billingPhone: 'Phone',
    billingPaymentMethod: 'Payment method',
    billingInvoices: 'Invoices',
    billingInvoiceFallback: 'Invoice',
    billingDownload: 'Download',
    billingNoInvoices: 'No invoices available.',
    billingPrevious: 'Previous',
    billingNext: 'Next',
    billingNeedAuth: 'You need to sign in to view billing.',
    billingLoadError: 'We could not load the billing information.',
    apiKeyTitle: 'API Key',
    apiKeyCardTitle: 'Your API key',
    apiKeyCardDesc: 'Use this key to connect the AAAQuest plugin to your account from AAAQuest > Set API KEY.',
    apiKeySecurityNote: 'If you need to copy the API key again, you must rotate it for security reasons.',
    apiKeyRotate: 'Rotate',
    apiKeyValueLabel: 'API key',
    apiKeyCopyCta: 'Copy to clipboard',
    apiKeyCopyAria: 'Copy API key',
    apiKeyShowAria: 'Show API key',
    apiKeyHideAria: 'Hide API key',
    apiKeyCopied: 'Copied to clipboard.',
    apiKeyNeedAuth: 'You need to sign in to view your API key.',
    apiKeyLoadError: 'We could not load your API key.',
    apiKeyCopyError: 'The API key could not be copied.',
    apiKeyRotateNeedAuth: 'You need to sign in to rotate your API key.',
    apiKeyRotateError: 'The API key could not be rotated.',
    apiKeyRotateModalTitle: 'Rotate API key',
    apiKeyRotateModalBody: 'If the API key is already linked in Figma, you will need to replace it manually. Are you sure you want to rotate the API key?',
    apiKeyRotateConfirm: 'Rotate API key',
    termsTitle: 'Terms and conditions',
    termsUpdated: 'Last updated: February 22, 2026.',
    termsSections: [
      {
        title: '1. Identification',
        body: 'AAAQuest is a digital platform owned by its holder (hereinafter, “AAAQuest”, “we”). You can contact us at victorsaizalfageme@gmail.com.',
      },
      {
        title: '2. Acceptance of terms',
        body: 'By accessing and using AAAQuest, you accept these terms and our privacy policy. If you do not agree, you must not use the platform.',
      },
      {
        title: '3. Registration and account',
        body: 'You must provide accurate information and keep it up to date. You are responsible for your account usage and for keeping your credentials confidential.',
      },
      {
        title: '4. Plans, payments, and billing',
        body: 'AAAQuest offers a free plan and paid plans. Prices and conditions are shown in the platform and may change. Purchases are processed through Stripe. Access to features depends on the active plan.',
      },
      {
        title: '5. Permitted use',
        body: 'You may not use the platform for unlawful purposes, infringe third-party rights, attempt to access other accounts, or perform actions that damage, alter, or compromise the service.',
      },
      {
        title: '6. Intellectual property',
        body: 'All AAAQuest content, brand, design, and software belong to its holder and are protected by intellectual property laws. No license is granted other than the normal use of the service.',
      },
      {
        title: '7. Limitation of liability',
        body: 'AAAQuest is provided “as is”. To the extent permitted by law, we do not guarantee the absence of errors or uninterrupted availability of the service. We will not be liable for indirect damages or data loss.',
      },
      {
        title: '8. Data protection',
        body: 'We process your data in accordance with applicable law and our privacy policy. You may request deletion of your account and data at any time.',
      },
      {
        title: '9. Data we collect',
        body: 'We collect the basic account and billing data needed to operate the service. Payment information and the user data tied to the subscription are managed through Stripe. When users send screenshots or design fragments for analysis, that content is transmitted only through the API to generate the documentation and is not stored.',
      },
      {
        title: '10. API key, feature access, and access control',
        body: 'The API key is used to identify the account and its plan in authorized integrations. Users may rotate or revoke it from the relevant product area; once rotated, the previous key should no longer be used. Text Resize may be used without credit validation, while accessibility detection requires a valid API key and available credits. Access is limited through reasonable authentication and authorization controls together with encryption in transit between clients and services.',
      },
      {
        title: '11. Rectification, deletion, and privacy contact',
        body: 'Users may request access, rectification, or deletion of their data by writing to privacidad@aaaquest.com or through /contact. We will handle requests under applicable law, including the general logic of GDPR and CCPA in plain language, without prejudice to retention needed for legal obligations, security, or fraud prevention.',
      },
      {
        title: '12. No-account access and responsible use',
        body: 'Some features may require an account, an active session, or an API key, while others may be offered in a limited mode without credit validation. The web panel requires an account for normal operation, and analysis features depend on the active plan or corresponding API key. Users agree not to upload screenshots containing unnecessary sensitive information, secrets, credentials, or third-party personal data unless they have a legitimate basis to do so.',
      },
      {
        title: '13. Changes',
        body: 'We may update these terms. If you continue using the platform after the changes, you will be deemed to accept the new terms.',
      },
      {
        title: '14. Governing law',
        body: 'These terms are governed by Spanish law. For any dispute, the parties submit to the courts of Barcelona, unless mandatory law provides otherwise.',
      },
    ],
    contactTitle: 'Contact us',
    contactSubtitle: 'Tell us what you need and we will get back to you as soon as possible.',
    contactAuthNotice: 'To send the message from your registered email, sign in first.',
    contactSubjectLabel: 'Subject',
    contactSubjectPlaceholder: 'Example: We need a demo for the team',
    contactSubjectRequired: 'Subject is required.',
    contactMessageLabel: 'Message',
    contactMessagePlaceholder: 'Tell us what you need, team size, deadlines, and any relevant context.',
    contactMessageRequired: 'Message is required.',
    contactNeedSignIn: 'Sign in to send from your registered email.',
    contactMailNoticePrefix: 'Your email client will open to send the message from',
    contactCancel: 'Cancel',
    contactSend: 'Send',
    contactDefaultUser: 'User',
    contactBodyMessageLabel: 'Message:',
    contactBodySentByLabel: 'Sent by',
    accountPhoneLabel: 'Phone',
    accountPhoneHint: 'International format with prefix (example +34 633 123 456)',
    accountSecurityDesc: 'Update your password to keep your account secure.',
    accountChangePassword: 'Change password',
    accountPhoneFormatError: 'Error saving the phone number. Use an international prefix with +, for example +34.',
    accountPhoneDuplicateError: 'Error saving the phone number. That phone number is already in use.',
    accountSaveError: 'Error saving changes.',
    accountPasswordModalTitle: 'Change password',
    accountSavePassword: 'Save password',
    analysisErrorUnsupportedFormat: 'Unsupported format. Use PNG, JPG, or WEBP.',
    analysisErrorTryAgain: 'Analysis failed. Please try again.',
    analysisErrorNoCredits: 'You have no credits available. Review your plan to keep analyzing.',
    analysisErrorCreditsSync: 'The analysis finished, but we could not update the credits.',
    checkerDropFile: 'Drop the file here',
    checkerPdfTitle: 'AAAQuest Audit',
    checkerPdfFooter: 'Made with Gemini 3 by Victor Saiz Alfageme',
    checkerPdfScreenSummary: 'Screen summary',
    checkerPdfGlobalAccessibility: 'Global accessibility',
    checkerPdfViewTitle: 'TITLE',
    checkerPdfStructure: 'STRUCTURE (Landmarks)',
    checkerPdfHeading: 'HEADINGS',
    checkerPdfFocusOrder: 'FOCUS ORDER',
    checkerPdfSectionsDetected: 'Detected sections / components',
    checkerPdfGeneral: 'General',
    checkerPdfBehavior: 'Behavior',
    checkerPdfAlt: 'Alt / Images',
    checkerPdfGrouped: 'Grouping',
    checkerSplitView: 'Split view',
    checkerFullView: 'Full view',
    checkerSwitchToSplitView: 'Switch to split view',
    checkerSwitchToFullView: 'Switch to full view',
    checkerDownloadPdf: 'Download PDF',
    checkerReportFileName: 'AAAQuest-Accessibility-Report.pdf',
    checkerSeeAll: 'See all',
    checkerNoSectionsDetected: 'No specific sections were detected.',
    signInEmailPlaceholder: 'you@email.com',
    testGenerateError: 'Error generating the test. Please try again.',
    testTopicRandom: 'Random',
    testTopicErrors: 'Detect errors',
    testTopicVoiceOver: 'VoiceOver reading',
    testTopicDocumentation: 'Complete documentation',
    testTopicFocusOrder: 'Focus order',
    testTopicHeadings: 'Hierarchy (Headings)',
    testTopicReflow: 'Reflow & Zoom',
    testTopicStates: 'States and errors',
    testTopicGrouping: 'Grouped elements',
    testTopicModals: 'Modals & overlays',
    difficultyBasic: 'Basic',
    difficultyIntermediate1: 'Intermediate 1',
    difficultyIntermediate2: 'Intermediate 2',
    difficultyAdvanced: 'Advanced',
    difficultyExpert: 'Expert / Very hard',
    studyScreenReaderMobileLabel: 'Mobile (VoiceOver/TalkBack)',
    studyScreenReaderDesktopLabel: 'Desktop (NVDA)',
    studyExperienceMobile: 'Touch experience (mobile)',
    studyExperienceDesktop: 'Keyboard experience (desktop)'
  },
  zh: {
    appTitle: 'AAAQuest',
    homeSubtitle: '带有游戏模式、快速测试和自动AI审计的数字无障碍学习平台。',
    studyMode: '学习模式',
    studyModeDesc: '通过游戏探索WCAG标准！',
    testMode: '考试时间',
    testModeDesc: '以20个问题的形式进行快速无障碍测试',
    checkerMode: '无障碍检测器',
    checkerModeDesc: '几秒钟内从屏幕截图生成无障碍文档',
    certMode: '证书',
    certModeDesc: '查看您的最终进度并获得专家文凭。',
    footer: '由 Gemini 3 制作，作者',
    changingLanguage: '正在更改语言...',
    navStudy: '学习',
    navTest: '测试',
    navChecker: '检测器',
    signIn: '登录',
    signUpNav: '注册',
    signUpTitle: '注册即可免费生成',
    signUpForFree: '免费注册',
    signUpPasswordTitle: '创建账户',
    signInPasswordTitle: '输入你的密码',
    signUpGoogle: '使用 Google 继续',
    signUpApple: '使用 Apple 继续',
    signUpSSO: '单点登录 (SSO)',
    signUpOr: '或',
    signUpEmailPlaceholder: '输入你的邮箱',
    signUpEmailButton: '使用邮箱继续',
    signUpTerms: '继续即表示你同意使用条款和隐私政策。',
    authCloseLabel: '关闭',
    signUpPanelTitle: '用 AI 生成无障碍',
    signUpPanelSubtitle: '文档几秒内就绪。',
    authContinue: '继续',
    authGenericError: '无法继续，请检查信息。',
    authCheckInbox: '如果该邮箱是新注册，请检查收件箱确认。',
    launchApp: '启动应用',
    signInTitle: '登录 AAAQuest',
    signInSubtitle: '登录或创建账户以保存进度。',
    signInEmailLabel: '邮箱',
    signInPasswordLabel: '密码',
    signInButton: '登录',
    signUpButton: '创建账户',
    signInGoogle: '使用 Google 继续',
    signOutButton: '退出登录',
    signInBack: '返回主页',
    signInSignedInAs: '已登录为',
    signInSuccess: '登录成功。',
    signUpSuccess: '账户已创建，请检查邮箱确认。',
    signInError: '登录失败。',
    signUpError: '创建账户失败。',
    signOutSuccess: '已退出登录。',
    signOutError: '无法退出登录。',
    studyTitle: '学习模式',
    studySubtitle: '通过游戏和AI生成的动态理论，从零开始学习无障碍知识',
    level: '等级',
    start: '开始',
    back: '返回',
    next: '下一步',
    confirm: '确认答案',
    levelComplete: '等级完成！',
    levelFailed: '等级失败',
    returnSelector: '返回开始',
    generatingLesson: '正在生成课程...',
    concepts: '核心概念',
    deepDive: '最佳实践',
    evaluation: '评估',
    visualExample: '组件示例',
    tabs: { nonTech: '功能规范', tech: '技术代码', voiceOver: '屏幕阅读器' },
    audioOutput: '音频输出',
    testTitle: '考试时间',
    testSubtitle: '这里有免费的无障碍测试，采用快速动态的20问格式',
    selectTopic: '选择主题',
    selectDifficulty: '难度等级',
    startTest: '开始测试',
    score: '得分',
    returnMenu: '返回菜单',
    generatingTest: '正在生成测试：',
    question: '问题',
    explanation: '解释',
    finish: '完成',
    completed: '测试完成',
    backBtn: '返回',
    checkerTitle: '无障碍检测器',
    checkerSubtitle: '上传文件即可生成无障碍文档',
    uploadTitle: '选择要记录的图像',
    uploadDesc: '选择数字产品的截图时，AI代理将对其进行分析以生成完整的无障碍文档。',
    analyzeBtn: '分析截图',
    analyzing: '正在分析...',
    analysisComplete: '分析完成',
    analysisIncomplete: '分析不完整',
    uploadAnother: '上传另一个文件',
    invalidUI: '未检测到有效的UI',
    invalidUIMsg: '上传的图像似乎不是数字产品的截图，或者无法正确分析。',
    tryAgain: '重试',
    context: '检测到的上下文',
    detectedScreen: '检测到的屏幕',
    device: '设备',
    sector: '行业',
    screenType: '屏幕类型',
    uploadFile: '上传文件',
    imageLoaded: '图片上传成功',
    clickToChange: '点击更改',
    preview: '预览',
    imagePlaceholder: '上传的图片将显示在这里',
    formats: 'PNG, JPG, WEBP',
    reference: '参考',
    incompleteDetails: '无法生成所有部分。请确保图像清晰并显示完整的界面。',
    contextOptional: '上下文 (可选)',
    contextPlaceholder: '例如：这是银行应用程序的购买确认模式...',
    estimatedTime: '预计时间：12秒',
    uiLanguage: '语言',
    componentCount: '检测到的组件',
    analysisIncorrect: '分析不正确',
    screenSize: '屏幕尺寸',
    layoutType: '布局',
    layoutSimple: '简单',
    layoutComplex: '复杂',
    sectionsComponents: '部分/组件',
    step: '步',
    learnMore: 'Learn More',
    homeInfoButton: 'Discover more info',
    specAccess: 'Specific Accessibility',
    behavior: 'Behavior',
    clickToExpand: '点击展开...',
    dos: '应做',
    donts: '不应做',
    correct: '正确',
    continue: '继续',
    locked: '锁定',
    bestScore: '最佳成绩',
    selectLevel: '选择等级',
    infoTitle: '数字无障碍：从零到英雄',
    infoSubtitle: '没有昂贵的课程。没有复杂的认证。免费学习并使用AI进行审计。',
    learnSectionTitle: '无限制免费学习',
    learnSectionDesc: '掌握NVDA、VoiceOver和TalkBack等基本工具。了解用户如何在不依赖视觉的情况下真正进行导航。',
    infoLevel1: "等级 1",
    infoFoundations: "基础知识",
    infoLearnList: [
      "无障碍包容性学习",
      "AI根据你的水平生成动态内容",
      "通过游戏学习WCAG 2.1，没有枯燥的理论"
    ],
    auditSectionTitle: '智能审计与自动化审计',
    auditSectionDesc: '像Axe或Wave这样的工具对代码非常有用，但AAAQuest理解视觉上下文。立即生成设计的完整PDF报告。',
    ctaStudy: '开始学习',
    ctaChecker: '启动AI检测器',
    whyBetter: '为什么使用AI检测器？',
    whyBetterList: [
      '上下文视觉分析，不仅仅是代码语法',
      '自动生成PDF以交付给客户',
      '检测复杂组件（模态框、表格）',
      '免费且无需安装扩展'
    ],
    seoTitle: 'SEO & 无障碍：隐形链接',
    seoDesc: 'Google不“看”你的网络，而是“读”它。就像屏幕阅读器用户一样。语义正确的无障碍网络是搜索引擎优化的完美网络。结构、标题和ARIA标签提高你的排名。',
    srTitle: '真实与理论导航',
    srDesc: '自动化工具检测30%的错误。其余70%取决于用户体验。焦点是否按逻辑顺序移动？是否宣布模态框？学习验证真实的用户体验。',
    nvdaTitle: "NVDA",
    nvdaSubtitle: "Windows 标准",
    nvdaDesc: "Windows 标准。测试桌面系统真实体验必不可少。开源。",
    voiceOverTitle: "VoiceOver",
    voiceOverSubtitle: "macOS / iOS",
    voiceOverDesc: "原生集成。移动端手势导航，桌面端复杂快捷键。",
    talkBackTitle: "TalkBack",
    talkBackSubtitle: "Android",
    talkBackDesc: "世界上使用最广泛的阅读器。线性触摸控制和触摸探索。",
    techStackTitle: '未来的技术栈',
    techStackDesc: '告别缓慢的手动审计。AAAQuest结合计算机视觉（Gemini 2.5）和WCAG 2.1专家知识，加速你的设计和开发工作流程。',
    aboutTitle: '这背后是谁？',
    aboutName: 'Victor Saiz Alfageme',
    aboutRole: '产品设计师',
    aboutDesc: '我是一名转行为产品设计师的前开发人员。出于市场需求，我深入研究了无障碍领域，并创建了这个平台，看看Gemini 3在为我的小社区提供此类产品方面能走多远。',
    comingSoon: "更多功能即将推出",
    supportBtn: 'Support the project',
    pricingTitle: 'Plans & pricing',
    pricingSubtitle: 'Choose the plan that fits your pace.',
    pricingStarterName: 'Starter',
    pricingStarterDesc: 'Perfect to begin with monthly tokens.',
    pricingStarterCta: 'Choose Starter',
    pricingProName: 'Pro',
    pricingProDesc: 'Built for teams and frequent use.',
    pricingProCta: 'Choose Pro',
    pricingEnterpriseName: 'Enterprise',
    pricingEnterpriseDesc: 'Custom solutions and dedicated support.',
    pricingEnterpriseCta: 'Contact us',
    pricingPopular: 'Popular',
    pricingContactOnly: 'Contact us',
    pricingPerMonth: '/ month',
    pricingTokensPerMonth: 'tokens / month',
    pricingPerToken: 'per token',
    attemptsRemaining: '剩余尝试次数',
    limitReached: '达到限制',
    certTitle: 'AAAQuest 认证',
    certSubtitle: '您已展示出对 WCAG 2.1 无障碍准则的卓越掌握。',
    downloadCert: '下载文凭',
    certName: '无障碍专家',
    certHeader: '结业证书',
    certVerify: '特此证明',
    enterName: '请输入证书上的姓名：',
    certText: '成功完成数字产品数字无障碍和包容性设计的强化培训演示级别。',
    lockedTitle: 'You have used all available screenshot analysis attempts',
    lockedDesc: 'You can request more credits by writing to the tool creator Victor Saiz Alfageme via LinkedIn or via email victorsaizalfageme@gmail.com',
    lockedThanks: 'Thanks for using AAAQuest Accessibility Detector!'
  },
  ru: {
    appTitle: 'AAAQuest',
    homeSubtitle: 'Платформа для изучения цифровой доступности с игровыми режимами и ИИ-аудитом.',
    studyMode: 'Режим обучения',
    studyModeDesc: 'Изучайте критерии WCAG через игры!',
    testMode: 'Время теста',
    testModeDesc: 'Быстрые тесты по доступности в формате 20 вопросов',
    checkerMode: 'Детектор доступности',
    checkerModeDesc: 'Создайте документацию по доступности по скриншоту за секунды',
    certMode: 'Сертификат',
    certModeDesc: 'Просмотрите свой прогресс и получите диплом эксперта.',
    footer: 'Сделано с Gemini 3 от',
    changingLanguage: 'Смена языка...',
    navStudy: 'Учеба',
    navTest: 'Тест',
    navChecker: 'Проверка',
    signIn: 'Войти',
    signUpNav: 'Регистрация',
    signUpTitle: 'Зарегистрируйтесь, чтобы создавать бесплатно',
    signUpForFree: 'Зарегистрируйтесь бесплатно',
    signUpPasswordTitle: 'Создайте аккаунт',
    signInPasswordTitle: 'Введите пароль',
    signUpGoogle: 'Продолжить через Google',
    signUpApple: 'Продолжить через Apple',
    signUpSSO: 'Единый вход (SSO)',
    signUpOr: 'ИЛИ',
    signUpEmailPlaceholder: 'Введите email',
    signUpEmailButton: 'Продолжить с Email',
    signUpTerms: 'Продолжая, вы соглашаетесь с Условиями использования и Политикой конфиденциальности.',
    authCloseLabel: 'Закрыть',
    signUpPanelTitle: 'Создавайте доступность с ИИ',
    signUpPanelSubtitle: 'Документация готова за секунды.',
    authContinue: 'Продолжить',
    authGenericError: 'Не удалось продолжить. Проверьте данные.',
    authCheckInbox: 'Если это новый email, проверьте почту для подтверждения.',
    launchApp: 'Запустить приложение',
    signInTitle: 'Вход в AAAQuest',
    signInSubtitle: 'Войдите или создайте аккаунт, чтобы сохранить прогресс.',
    signInEmailLabel: 'Email',
    signInPasswordLabel: 'Пароль',
    signInButton: 'Войти',
    signUpButton: 'Создать аккаунт',
    signInGoogle: 'Продолжить через Google',
    signOutButton: 'Выйти',
    signInBack: 'Вернуться на главную',
    signInSignedInAs: 'Вы вошли как',
    signInSuccess: 'Вход выполнен.',
    signUpSuccess: 'Аккаунт создан. Проверьте почту для подтверждения.',
    signInError: 'Не удалось войти.',
    signUpError: 'Не удалось создать аккаунт.',
    signOutSuccess: 'Вы вышли.',
    signOutError: 'Не удалось выйти.',
    studyTitle: 'Режим обучения',
    studySubtitle: 'Изучайте доступность с нуля с помощью игр и динамической теории от ИИ',
    level: 'Уровень',
    start: 'Начать',
    back: 'Назад',
    next: 'Далее',
    confirm: 'Подтвердить',
    levelComplete: 'Уровень пройден!',
    levelFailed: 'Уровень провален',
    returnSelector: 'Вернуться в начало',
    generatingLesson: 'Создание урока...',
    concepts: 'Ключевые концепции',
    deepDive: 'Лучшие практики',
    evaluation: 'Оценка',
    visualExample: 'Пример компонента',
    tabs: { nonTech: 'Функциональная Спек.', tech: 'Технический Код', voiceOver: 'Скринридеры' },
    audioOutput: 'Аудиовыход',
    testTitle: 'Время теста',
    testSubtitle: 'Бесплатные тесты по доступности в быстром и динамичном формате',
    selectTopic: 'Выберите тему',
    selectDifficulty: 'Уровень сложности',
    startTest: 'Начать тест',
    score: 'Счет',
    returnMenu: 'В меню',
    generatingTest: 'Создание теста по',
    question: 'Вопрос',
    explanation: 'Объяснение',
    finish: 'Завершить',
    completed: 'Тест завершен',
    backBtn: 'Назад',
    checkerTitle: 'Детектор доступности',
    checkerSubtitle: 'Генератор документации по доступности из одного файла',
    uploadTitle: 'Выберите изображение',
    uploadDesc: 'При выборе скриншота цифрового продукта ИИ-агент проанализирует его для создания полной документации.',
    analyzeBtn: 'Анализировать',
    analyzing: 'Анализ...',
    analysisComplete: 'Анализ завершен',
    analysisIncomplete: 'Анализ неполный',
    uploadAnother: 'Загрузить другой файл',
    invalidUI: 'UI не обнаружен',
    invalidUIMsg: 'Изображение не похоже на скриншот цифрового продукта или не может быть проанализировано.',
    tryAgain: 'Попробовать снова',
    context: 'Контекст',
    detectedScreen: 'Экран',
    device: 'Устройство',
    sector: 'Сектор',
    screenType: 'Тип экрана',
    uploadFile: 'Загрузить файл',
    imageLoaded: 'Изображение загружено',
    clickToChange: 'Нажмите для замены',
    preview: 'Предпросмотр',
    imagePlaceholder: 'Изображение появится здесь',
    formats: 'PNG, JPG, WEBP',
    reference: 'Ссылка',
    incompleteDetails: 'Не удалось создать все разделы. Убедитесь, что изображение четкое.',
    contextOptional: 'Контекст (необязательно)',
    contextPlaceholder: 'Прим.: Это модальное окно подтверждения покупки для банковского приложения...',
    estimatedTime: 'Примерное время: 12 сек',
    uiLanguage: 'Язык',
    componentCount: 'Обнаружено элементов',
    analysisIncorrect: 'Анализ неверен',
    screenSize: 'Размер экрана',
    layoutType: 'Макет',
    layoutSimple: 'Простой',
    layoutComplex: 'Сложный',
    sectionsComponents: 'Разделы/Компоненты',
    step: 'Шаг',
    learnMore: 'Learn More',
    homeInfoButton: 'Discover more info',
    specAccess: 'Specific Accessibility',
    behavior: 'Behavior',
    clickToExpand: 'Нажмите, чтобы развернуть...',
    dos: 'Делать',
    donts: 'Не делать',
    correct: 'Верно',
    continue: 'Продолжить',
    locked: 'Заблокировано',
    bestScore: 'Лучший счет',
    selectLevel: 'Выберите уровень',
    infoTitle: 'Цифровая доступность: с нуля до героя',
    infoSubtitle: 'Никаких дорогих курсов. Никаких сложных сертификатов. Учитесь бесплатно и проводите аудит с ИИ.',
    learnSectionTitle: 'Учитесь бесплатно без ограничений',
    learnSectionDesc: 'Освойте основные инструменты, такие как NVDA, VoiceOver, и TalkBack. Поймите, как пользователи навигаруют без зрения.',
    infoLevel1: "УРОВЕНЬ 1",
    infoFoundations: "Основы",
    infoLearnList: [
      "Учитесь инклюзивно без комплексов",
      "Динамический контент от ИИ под ваш уровень",
      "Изучайте WCAG 2.1 играя, без скучной теории"
    ],
    auditSectionTitle: 'Интеллектуальный аудит против автоматического',
    auditSectionDesc: 'Инструменты типа Axe или Wave отличны для кода, но AAAQuest понимает визуальный контекст. Создавайте полные PDF-отчеты мгновенно.',
    ctaStudy: 'Начать обучение',
    ctaChecker: 'Запустить ИИ-детектор',
    whyBetter: 'Почему ИИ-детектор?',
    whyBetterList: [
      'Контекстуальный визуальный анализ, а не просто синтаксис кода',
      'Автоматическое создание PDF для клиентов',
      'Обнаружение сложных компонентов (модальные окна, таблицы)',
      'Бесплатно и без установки расширений'
    ],
    seoTitle: 'SEO и Доступность: Невидимая связь',
    seoDesc: 'Google не "видит" ваш сайт, он его "читает". Как и пользователь скринридера. Семантически правильный сайт для доступности идеально оптимизирован для поисковых систем.',
    srTitle: 'Реальная vs Теоретическая навигация',
    srDesc: 'Автоматические инструменты находят 30% ошибок. Остальные 70% зависят от UX. Логичен ли порядок фокуса? Озвучиваются ли модальные окна?',
    nvdaTitle: "NVDA",
    nvdaSubtitle: "Стандарт Windows",
    nvdaDesc: "Стандарт Windows. Необходим для тестирования реального опыта на десктопе. Open Source.",
    voiceOverTitle: "VoiceOver",
    voiceOverSubtitle: "macOS / iOS",
    voiceOverDesc: "Встроен нативно. Жестовая навигация на мобильных и сложные шорткаты на десктопе.",
    talkBackTitle: "TalkBack",
    talkBackSubtitle: "Android",
    talkBackDesc: "Самый популярный ридер в мире. Линейное сенсорное управление и изучение касанием.",
    techStackTitle: 'Технологический стек будущего',
    techStackDesc: 'Оставьте медленные ручные аудиты. AAAQuest объединяет компьютерное зрение (Gemini 2.5) с экспертными знаниями WCAG 2.1.',
    aboutTitle: 'Кто за этим стоит?',
    aboutName: 'Victor Saiz Alfageme',
    aboutRole: 'Дизайнер продуктов',
    aboutDesc: 'Я бывший разработчик, ставший дизайнером продуктов. Из-за потребностей рынка я глубоко погрузился в доступность и создал эту платформу, чтобы увидеть возможности Gemini 3 для моего небольшого сообщества.',
    comingSoon: "Скоро появятся новые функции",
    supportBtn: 'Support the project',
    pricingTitle: 'Plans & pricing',
    pricingSubtitle: 'Choose the plan that fits your pace.',
    pricingStarterName: 'Starter',
    pricingStarterDesc: 'Perfect to begin with monthly tokens.',
    pricingStarterCta: 'Choose Starter',
    pricingProName: 'Pro',
    pricingProDesc: 'Built for teams and frequent use.',
    pricingProCta: 'Choose Pro',
    pricingEnterpriseName: 'Enterprise',
    pricingEnterpriseDesc: 'Custom solutions and dedicated support.',
    pricingEnterpriseCta: 'Contact us',
    pricingPopular: 'Popular',
    pricingContactOnly: 'Contact us',
    pricingPerMonth: '/ month',
    pricingTokensPerMonth: 'tokens / month',
    pricingPerToken: 'per token',
    attemptsRemaining: 'попыток осталось',
    limitReached: 'Лимит исчерпан',
    certTitle: 'Сертификация AAAQuest',
    certSubtitle: 'Вы продемонстрировали исключительное владение рекомендациями по доступности WCAG 2.1.',
    downloadCert: 'Скачать диплом',
    certName: 'Эксперт по доступности',
    certHeader: 'СЕРТИФИКАТ О ЗАВЕРШЕНИИ',
    certVerify: 'Настоящим удостоверяется, что',
    enterName: 'Пожалуйста, введите ваше имя для сертификата:',
    certText: 'За успешное прохождение демо-уровней интенсивного обучения цифровой доступности и инклюзивному дизайну в цифровых продуктах.',
    lockedTitle: 'You have used all available screenshot analysis attempts',
    lockedDesc: 'You can request more credits by writing to the tool creator Victor Saiz Alfageme via LinkedIn or via email victorsaizalfageme@gmail.com',
    lockedThanks: 'Thanks for using AAAQuest Accessibility Detector!'
  },
  hi: {
    appTitle: 'AAAQuest',
    homeSubtitle: 'गेम मोड और स्वचालित AI ऑडिट के साथ डिजिटल एक्सेसिबिलिटी लर्निंग प्लेटफॉर्म।',
    studyMode: 'अध्ययन मोड',
    studyModeDesc: 'गेम्स के माध्यम से WCAG मानदंडों का अन्वेषण करें!',
    testMode: 'परीक्षा का समय',
    testModeDesc: '20-प्रश्नों के प्रारूप में त्वरित एक्सेसिबिलिटी परीक्षण लें',
    checkerMode: 'एक्सेसिबिलिटी डिटेक्टर',
    checkerModeDesc: 'सेकंड में स्क्रीनशॉट से एक्सेसिबिलिटी दस्तावेज़ बनाएं',
    certMode: 'प्रमाणपत्र',
    certModeDesc: 'अपनी अंतिम प्रगति देखें और अपना विशेषज्ञ डिप्लोमा प्राप्त करें।',
    footer: 'Gemini 3 द्वारा निर्मित',
    changingLanguage: 'भाषा बदली जा रही है...',
    navStudy: 'अध्ययन',
    navTest: 'टेस्ट',
    navChecker: 'चेकर',
    signIn: 'साइन इन',
    signUpNav: 'साइन अप',
    signUpTitle: 'मुफ्त में जनरेट करने के लिए साइन अप करें',
    signUpForFree: 'मुफ्त में साइन अप करें',
    signUpPasswordTitle: 'अपना खाता बनाएं',
    signInPasswordTitle: 'अपना पासवर्ड डालें',
    signUpGoogle: 'Google से जारी रखें',
    signUpApple: 'Apple से जारी रखें',
    signUpSSO: 'सिंगल साइन-ऑन (SSO)',
    signUpOr: 'या',
    signUpEmailPlaceholder: 'अपना ईमेल दर्ज करें',
    signUpEmailButton: 'ईमेल से जारी रखें',
    signUpTerms: 'जारी रखने पर आप उपयोग की शर्तें और गोपनीयता नीति से सहमत होते हैं।',
    authCloseLabel: 'बंद करें',
    signUpPanelTitle: 'AI से एक्सेसिबिलिटी बनाएं',
    signUpPanelSubtitle: 'आपकी डॉक्यूमेंटेशन कुछ सेकंड में तैयार।',
    authContinue: 'जारी रखें',
    authGenericError: 'जारी नहीं रख सके। कृपया विवरण जांचें।',
    authCheckInbox: 'यदि यह नया ईमेल है, तो पुष्टि के लिए इनबॉक्स देखें।',
    launchApp: 'ऐप लॉन्च करें',
    signInTitle: 'AAAQuest में साइन इन',
    signInSubtitle: 'लॉगिन करें या खाता बनाएं ताकि प्रगति सेव हो सके।',
    signInEmailLabel: 'ईमेल',
    signInPasswordLabel: 'पासवर्ड',
    signInButton: 'साइन इन',
    signUpButton: 'खाता बनाएं',
    signInGoogle: 'Google से जारी रखें',
    signOutButton: 'साइन आउट',
    signInBack: 'होम पर वापस',
    signInSignedInAs: 'के रूप में साइन इन',
    signInSuccess: 'साइन इन सफल।',
    signUpSuccess: 'खाता बन गया। कृपया ईमेल सत्यापित करें।',
    signInError: 'साइन इन नहीं हो सका।',
    signUpError: 'खाता नहीं बन सका।',
    signOutSuccess: 'साइन आउट सफल।',
    signOutError: 'साइन आउट नहीं हो सका।',
    studyTitle: 'अध्ययन मोड',
    studySubtitle: 'गेम्स और गतिशील AI सिद्धांत के माध्यम से शून्य से हीरो तक एक्सेसिबिलिटी सीखें',
    level: 'स्तर',
    start: 'शुरू करें',
    back: 'वापस',
    next: 'अगला',
    confirm: 'उत्तर की पुष्टि करें',
    levelComplete: 'स्तर पूरा हुआ!',
    levelFailed: 'स्तर असफल',
    returnSelector: 'शुरुआत में वापस जाएँ',
    generatingLesson: 'पाठ तैयार हो रहा है...',
    concepts: 'प्रमुख अवधारणाएं',
    deepDive: 'सर्वोत्तम प्रथाएं',
    evaluation: 'मूल्यांकन',
    visualExample: 'घटक उदाहरण',
    tabs: { nonTech: 'कार्यात्मक विवरण', tech: 'तकनीकी कोड', voiceOver: 'स्क्रीन रीडर' },
    audioOutput: 'ऑडियो आउटपुट',
    testTitle: 'परीक्षा का समय',
    testSubtitle: 'यहाँ त्वरित और गतिशील 20-प्रश्न प्रारूप में मुफ्त एक्सेसिबिलिटी परीक्षण हैं',
    selectTopic: 'एक विषय चुनें',
    selectDifficulty: 'कठिनाई स्तर',
    startTest: 'टेस्ट शुरू करें',
    score: 'स्कोर',
    returnMenu: 'मेनू पर वापस जाएं',
    generatingTest: 'टेस्ट तैयार हो रहा है: ',
    question: 'प्रश्न',
    explanation: 'व्याख्या',
    finish: 'समाप्त',
    completed: 'टेस्ट पूरा हुआ',
    backBtn: 'वापस',
    checkerTitle: 'एक्सेसिबिलिटी डिटेक्टर',
    checkerSubtitle: 'बस एक फ़ाइल अपलोड करके एक्सेसिबिलिटी दस्तावेज़ जनरेटर',
    uploadTitle: 'दस्तावेज़ करने के लिए एक छवि चुनें',
    uploadDesc: 'डिजिटल उत्पाद का स्क्रीनशॉट चुनने पर, पूर्ण एक्सेसिबिलिटी दस्तावेज़ीकरण के लिए AI एजेंट द्वारा इसका विश्लेषण किया जाएगा।',
    analyzeBtn: 'स्क्रीनशॉट का विश्लेषण करें',
    analyzing: 'विश्लेषण हो रहा है...',
    analysisComplete: 'विश्लेषण पूरा हुआ',
    analysisIncomplete: 'विश्लेषण अधूरा',
    uploadAnother: 'दूसरी फ़ाइल अपलोड करें',
    invalidUI: 'कोई मान्य UI नहीं मिला',
    invalidUIMsg: 'अपलोड की गई छवि डिजिटल उत्पाद का स्क्रीनशॉट नहीं लगती या सही ढंग से विश्लेषण नहीं की जा सकी।',
    tryAgain: 'पुनः प्रयास करें',
    context: 'पता लगाया गया संदर्भ',
    detectedScreen: 'पता लगाया गया स्क्रीन',
    device: 'उपकरण',
    sector: 'क्षेत्र',
    screenType: 'स्क्रीन प्रकार',
    uploadFile: 'फ़ाइल अपलोड करें',
    imageLoaded: 'छवि सफलतापूर्वक अपलोड की गई',
    clickToChange: 'बदलने के लिए क्लिक करें',
    preview: 'पूर्वावलोकन',
    imagePlaceholder: 'अपलोड की गई छवि यहां दिखाई देगी',
    formats: 'PNG, JPG, WEBP',
    reference: 'संदर्भ',
    incompleteDetails: 'सभी अनुभाग उत्पन्न नहीं किए जा सके। सुनिश्चित करें कि छवि स्पष्ट है।',
    contextOptional: 'संदर्भ (वैकल्पिक)',
    contextPlaceholder: 'उदाहरण: यह एक बैंकिंग ऐप के लिए खरीद पुष्टि मोडल है...',
    estimatedTime: 'अनुमानित समय: 12s',
    uiLanguage: 'भाषा',
    componentCount: 'पता लगाए गए घटक',
    analysisIncorrect: 'विश्लेषण गलत',
    screenSize: 'स्क्रीन का आकार',
    layoutType: 'लेआउट',
    layoutSimple: 'साधारण',
    layoutComplex: 'जटिल',
    sectionsComponents: 'अनुभाग/घटक',
    step: 'कदम',
    learnMore: 'Learn More',
    homeInfoButton: 'Discover more info',
    specAccess: 'Specific Accessibility',
    behavior: 'Behavior',
    clickToExpand: 'विस्तार करने के लिए क्लिक करें...',
    dos: 'क्या करें',
    donts: 'क्या न करें',
    correct: 'सही',
    continue: 'जारी रखें',
    locked: 'बंद',
    bestScore: 'सर्वोत्तम स्कोर',
    selectLevel: 'एक स्तर चुनें',
    infoTitle: 'डिजिटल एक्सेसिबिलिटी: जीरो टू हीरो',
    infoSubtitle: 'कोई महंगे पाठ्यक्रम नहीं। कोई जटिल प्रमाणन नहीं। मुफ्त में सीखें और AI के साथ ऑडिट करें।',
    learnSectionTitle: 'बिना सीमा के मुफ्त सीखें',
    learnSectionDesc: 'NVDA, VoiceOver और TalkBack जैसे आवश्यक उपकरणों में महारत हासिल करें। समझें कि उपयोगकर्ता वास्तव में दृष्टि पर भरोसा किए बिना कैसे नेविगेट करते हैं।',
    infoLevel1: "स्तर 1",
    infoFoundations: "बुनियादी आधार",
    infoLearnList: [
      "बिना किसी जटिलता के समावेशी रूप से सीखें",
      "आपके स्तर के आधार पर AI द्वारा उत्पन्न गतिशील सामग्री",
      "बोरिंग सिद्धांत के बिना, खेल-खेल में WCAG 2.1 सीखें"
    ],
    auditSectionTitle: 'बुद्धिमान बनाम स्वचालित ऑडिट',
    auditSectionDesc: 'Axe या Wave जैसे उपकरण कोड के लिए महान हैं, लेकिन AAAQuest दृश्य संदर्भ को समझता है। तुरंत अपने डिजाइनों की पूर्ण PDF रिपोर्ट तैयार करें।',
    ctaStudy: 'सीखना शुरू करें',
    ctaChecker: 'AI डिटेक्टर लॉन्च करें',
    whyBetter: 'AI डिटेक्टर का उपयोग क्यों करें?',
    whyBetterList: [
      'संदर्भित दृश्य विश्लेषण, केवल कोड सिंटैक्स नहीं',
      'ग्राहक वितरण के लिए स्वचालित PDF पीढ़ी',
      'जटिल घटकों (मोडल, टेबल्स) का पता लगाना',
      'मुफ्त और कोई एक्सटेंशन इंस्टॉलेशन की आवश्यकता नहीं है'
    ],
    seoTitle: 'SEO और एक्सेसिबिलिटी',
    seoDesc: 'Google आपके वेब को "देखता" नहीं है, वह इसे "पढ़ता" है। एक्सेसिबिलिटी के लिए एक सिमेंटिक रूप से सही वेब सर्च इंजन के लिए पूरी तरह से अनुकूलित है।',
    srTitle: 'वास्तविक बनाम सैद्धांतिक नेविगेशन',
    srDesc: 'स्वचालित उपकरण 30% त्रुटियों का पता लगाते हैं। शेष 70% उपयोगकर्ता अनुभव पर निर्भर करता है। क्या फोकस तार्किक क्रम में यात्रा करता है?',
    nvdaTitle: "NVDA",
    nvdaSubtitle: "विंडोज़ मानक",
    nvdaDesc: "विंडोज़ मानक। डेस्कटॉप सिस्टम पर वास्तविक अनुभव का परीक्षण करने के लिए आवश्यक। ओपन सोर्स।",
    voiceOverTitle: "VoiceOver",
    voiceOverSubtitle: "macOS / iOS",
    voiceOverDesc: "मूल रूप से एकीकृत। मोबाइल पर जेस्चर नेविगेशन और डेस्कटॉप पर जटिल शॉर्टकट।",
    talkBackTitle: "TalkBack",
    talkBackSubtitle: "Android",
    talkBackDesc: "दुनिया का सबसे अधिक उपयोग किया जाने वाला रीडर। रैखिक स्पर्श नियंत्रण और स्पर्श अन्वेषण।",
    techStackTitle: 'भविष्य का टेक स्टैक',
    techStackDesc: 'धीमे मैनुअल ऑडिट को पीछे छोड़ दें। AAAQuest कंप्यूटर विज़न (Gemini 2.5) को WCAG 2.1 विशेषज्ञ ज्ञान के साथ जोड़ता है।',
    aboutTitle: 'इसके पीछे कौन है?',
    aboutName: 'Victor Saiz Alfageme',
    aboutRole: 'उत्पाद डिजाइनर',
    aboutDesc: 'मैं एक पूर्व डेवलपर हूँ जो प्रोडक्ट डिज़ाइनर बन गया हूँ। बाजार की आवश्यकता के कारण, मैंने एक्सेसिबिलिटी में गहराई से गोता लगाया और इस प्लेटफॉर्म को बनाया ताकि यह देख सकूं कि जेमिनी 3 मेरे छोटे comunidad के लिए ऐसा उत्पाद प्रदान करने में कितनी दूर तक जा सकता है।',
    comingSoon: "जल्द ही और अधिक सुविधाएँ आ रही हैं",
    supportBtn: 'Support the project',
    pricingTitle: 'Plans & pricing',
    pricingSubtitle: 'Choose the plan that fits your pace.',
    pricingStarterName: 'Starter',
    pricingStarterDesc: 'Perfect to begin with monthly tokens.',
    pricingStarterCta: 'Choose Starter',
    pricingProName: 'Pro',
    pricingProDesc: 'Built for teams and frequent use.',
    pricingProCta: 'Choose Pro',
    pricingEnterpriseName: 'Enterprise',
    pricingEnterpriseDesc: 'Custom solutions and dedicated support.',
    pricingEnterpriseCta: 'Contact us',
    pricingPopular: 'Popular',
    pricingContactOnly: 'Contact us',
    pricingPerMonth: '/ month',
    pricingTokensPerMonth: 'tokens / month',
    pricingPerToken: 'per token',
    attemptsRemaining: 'शेष प्रयास',
    limitReached: 'सीमा समाप्त',
    certTitle: 'AAAQuest प्रमाणन',
    certSubtitle: 'आपने WCAG 2.1 एक्सेसिबिलिटी दिशानिर्देशों की असाधारण महारत का प्रदर्शन किया है।',
    downloadCert: 'डिप्लोमा डाउनलोड करें',
    certName: 'एक्सेसिबिलिटी विशेषज्ञ',
    certHeader: 'पूर्णता का प्रमाण पत्र',
    certVerify: 'यह प्रमाणित करता है कि',
    enterName: 'कृपया प्रमाण पत्र के लिए अपना नाम दर्ज करें:',
    certText: 'डिजिटल उत्पादों में डिजिटल एक्सेसिबिलिटी और समावेशी डिज़ाइन में गहन प्रशिक्षण के डेमो स्तरों को सफलतापूर्वक पूरा करने के लिए।',
    lockedTitle: 'You have used all available screenshot analysis attempts',
    lockedDesc: 'You can request more credits by writing to the tool creator Victor Saiz Alfageme via LinkedIn or via email victorsaizalfageme@gmail.com',
    lockedThanks: 'Thanks for using AAAQuest Accessibility Detector!'
  }
};
